import React, { useState, useCallback, useRef, useEffect } from 'react';
import { C, F, S, W, SP, R, BG_FIELD } from './tokens';
import { ExplanationsProvider, InfoMarker, MethodologyPage, usePath, METHODOLOGY_PATH } from './Explain';
import { LimitationsPage, LIMITATIONS_PATH } from './Limitations';
import { useRouteHead } from './head';
import { QuestionBox, QuestionContextProvider, QuestionsAnswersPage, QUESTIONS_PATH,
         useQuestionPublisher } from './Questions';

// The dev server runs on localhost:3000 and this default points at localhost:5000, NOT
// 127.0.0.1:5000. Those are the same machine but DIFFERENT SITES to a browser, and a
// SameSite=Lax session cookie set by one is never sent to the other - which is exactly
// why signing in appeared to work while every refresh came back signed out. Cookies
// ignore the port, so localhost:3000 -> localhost:5000 is same-site and the session
// survives. Do not change this back to 127.0.0.1.
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
// How long a request may run before the page says anything about it. Fifteen seconds, and what
// it then says is deliberately NEUTRAL: it describes the work, not the state of the server.
//
// WHY THE OLD VERSION WAS REMOVED. It asked /api/health at five seconds and turned the answer
// into an explanation - "other analyses are running ahead of this one" when the busy counters
// were non-zero, "the server was asleep" when health did not answer. Both were inferences, and
// the live site showed the queue one to a reader who was alone on it: a cold analysis on the free
// instance is simply slow, and a fast health check next to a slow analysis does not mean a queue.
// A reader cannot act on either explanation, and being told the wrong one is worse than being
// told nothing. So there is one sentence, it is true whatever the cause, and it never guesses.
const SLOW_NOTICE_MS = 15000;
const SLOW_NOTICE = 'Please wait while the figures are retrieved and modelled. This can take up '
                  + 'to a minute.';

// Product identity. Every user-visible surface that names the product reads this rather
// than hardcoding a string, so a rename is a one-line change here.
const PRODUCT_NAME = 'Dealquire';

// What a reader sees when the server refuses the request because it is already at capacity - the
// 503 the heavy-work gate returns. It says the product is under load and what to do about it, and
// it says nothing about queues, positions or other users, because none of that is actionable.
const AT_CAPACITY = `${PRODUCT_NAME} is handling a high volume of requests right now. Please try `
                  + 'again in a minute.';

// ---------------------------------------------------------------------------
// Ticker autocomplete. Filters a client-side list, so it responds instantly with
// no request per keystroke. Match priority: exact symbol, symbol prefix, symbol
// substring, then company name - a user typing "paint" should find Asian Paints.
// ---------------------------------------------------------------------------
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[n];
}

function closestTicker(input, tickers) {
  const q = (input || '').toUpperCase().trim();
  if (!q || q.length < 3 || !tickers.length) return null;
  let best = null, bestD = Infinity;
  for (const [sym] of tickers) {
    if (Math.abs(sym.length - q.length) > 3) continue;
    const d = levenshtein(q, sym);
    if (d < bestD) { bestD = d; best = sym; }
  }
  return bestD <= 2 ? best : null;
}

// ONE NORMALISATION FOR SEARCH: letters and digits only, upper case. It is what makes
// "tata m", "tatamotors" and "tata motors" the same query, and what lets a query match a
// company name that carries spaces, full stops and ampersands.
const searchKey = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

function TickerAutocomplete({ value, onChange, onSubmit, placeholder, tickers,
                             aliases = {}, style, disabled }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);

  const q = searchKey(value);
  // A SYMBOL THAT NO LONGER EXISTS. Typed in full, it offers what replaced it and says why.
  const alias = q && aliases[q] ? aliases[q] : null;
  const matches = React.useMemo(() => {
    if (!q || !tickers.length) return [];
    const aliasFirst = alias ? alias.symbols : [];
    const exact = [], prefix = [], sub = [], namePrefix = [], byName = [];
    for (const row of tickers) {
      const [sym, name] = row;
      if (aliasFirst.includes(sym)) continue;      // added at the top, below
      const k = searchKey(sym), n = searchKey(name);
      if (k === q) exact.push(row);
      else if (k.startsWith(q)) prefix.push(row);
      else if (k.includes(q)) sub.push(row);
      // THE NAME IS SEARCHED ON THE SAME KEY AS THE SYMBOL. A name that STARTS with the query
      // ranks above one that carries it in the middle, so "tata m" offers Tata Motors before
      // Rallis India. Within each group the roster's own order is kept, which is by symbol.
      else if (n.startsWith(q)) namePrefix.push(row);
      else if (n.includes(q)) byName.push(row);
      if (exact.length + prefix.length + sub.length
          + namePrefix.length + byName.length > 200) break;
    }
    const aliasRows = aliasFirst
      .map(s => tickers.find(r => r[0] === s))
      .filter(Boolean);
    return [...aliasRows, ...exact, ...prefix, ...sub, ...namePrefix, ...byName].slice(0, 8);
  }, [q, tickers, alias]);

  const pick = (sym) => { onChange(sym); setOpen(false); setHi(0); };

  const onKey = (e) => {
    if (!open || !matches.length) {
      if (e.key === 'Enter' && onSubmit) { e.preventDefault(); onSubmit(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => (h + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => (h - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(matches[hi][0]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div style={{ position:'relative', display:'inline-block' }}>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value.toUpperCase()); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKey}
        style={style}
      />
      {open && matches.length > 0 && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:50,
                      minWidth:'100%', maxWidth:'340px', backgroundColor:C.surface,
                      border:`1px solid ${C.line}`, borderRadius:R.sm, overflow:'hidden',
                      boxShadow:'0 8px 24px rgba(0,0,0,0.6)' }}>
          {alias && (
            <div style={{ padding:'8px 12px', borderBottom:`1px solid ${C.line}`,
                          color:C.textMuted, fontSize:S.micro, lineHeight:1.5,
                          textAlign:'left' }}>
              {alias.why}
            </div>
          )}
          {matches.map(([sym, name, series], i) => (
            <div key={sym}
                 onMouseDown={(e) => { e.preventDefault(); pick(sym); }}
                 onMouseEnter={() => setHi(i)}
                 style={{ padding:'8px 12px', cursor:'pointer', textAlign:'left',
                          backgroundColor: i === hi ? '#1e2f28' : 'transparent' }}>
              <div style={{ color:C.accent, fontSize:S.body, fontWeight:W.semi }}>
                {sym}
                {series && series !== 'EQ' && (
                  <span style={{ color:C.target, fontSize:S.micro, marginLeft:SP.sml }}>
                    {series}
                  </span>
                )}
              </div>
              <div style={{ color:C.textMuted, fontSize:S.micro, whiteSpace:'nowrap',
                            overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Shown on the empty state. Each is a deal that exercises a different path through
// the model, so they double as a one-click smoke test.
const EXAMPLE_DEALS = [
  ['RELIANCE',  'ASIANPAINT', 'Cross-sector, both large'],
  ['HDFCBANK',  'FEDERALBNK', 'Bank acquiring a bank'],
  ['MOTHERSON', 'BOSCHLTD',   'Automotive components'],
  ['RELIANCE',  'UNITDSPR',   'Premium to peer median'],
];

const CAPABILITIES = [
  ['Pro Forma Financials', 'A combined income statement and balance sheet at the premium and the mix of cash and shares you choose.'],
  ['Valuation',            'DCF, dividend discount and EV/EBITDA, plotted as a range against the offer price.'],
  ['Comparable Companies', 'Curated peer groups, screened on size, with the median and the premium the deal implies.'],
  ['Export',               'A ten-page PDF tearsheet and an Excel model built from the same figures.'],
];

const TABS = ['Overview', 'Financials', 'Valuation', 'Deal Analysis'];

const METRIC_DEFINITIONS = {
  'Market capitalisation':           'Market value of all shares in issue: price × shares outstanding.',
  'Current price':        'Last traded price of the stock on NSE.',
  'P/E ratio':            'Price-to-earnings: market capitalisation divided by last reported net income. Screener and Yahoo use trailing twelve-month diluted earnings, so their P/E won\'t match.',
  'P/B ratio':            'Price-to-book: market capitalisation divided by reported shareholders’ equity. It shows what the market pays over net assets.',
  'Beta':                 'How much the stock moves relative to the market. Above 1 moves more than the market; below 1 moves less.',
  'Dividend yield':       'Annual dividend per share divided by the share price, as a percentage.',
  '52-week high':             'Highest price the stock has traded at over the last 52 weeks.',
  '52-week low':              'Lowest price the stock has traded at over the last 52 weeks.',
  'Sector':               'Broad industry classification assigned by the exchange.',
  'Revenue':              'Income from the business before any costs. Also called turnover or net sales.',
  'Operating income':     'Operating profit before interest and tax, taken from the EBIT row. Where that row is missing, the Operating Income row is used and the figure is marked ‡.',
  'EBITDA':               'Earnings before interest, tax, depreciation and amortisation. A rough guide to the cash the operations generate.',
  'Net income':           'Profit after all costs, interest and tax. This is what belongs to shareholders.',
  'Total assets':         'Everything the company owns: current assets such as cash and receivables, plus non-current assets such as property and equipment.',
  'Total debt':           'All interest-bearing borrowings, short-term and long-term.',
  'Cash & equivalents':   'Cash on hand plus highly liquid short-term investments, available straight away.',
  'Cash & cash equivalents':   'Cash on hand and deposits that turn into cash within about three months. One of the two parts of the cash figure deducted in net debt.',
  'Short-term investments':    'Liquid investments outside cash equivalents, usually maturing within a year. They are part of the cash deducted in net debt.',
  'Cash including short-term investments': 'Cash & cash equivalents plus short-term investments. This is the cash figure deducted in net debt, enterprise value, EV/EBITDA, the DCF and credit impact.',
  'Restricted cash, reported but not deducted': 'Cash the company can\'t use freely, such as escrow and margin deposits. It is not deducted from net debt, which may therefore be understated by up to this amount.',
  'Net debt':                  'Total debt minus cash including short-term investments. A negative figure means net cash.',
  'Shareholders\' equity':  'The company\'s net worth: total assets minus total liabilities. What shareholders would receive if the assets were sold off.',
  'Operating cash flow':  'Cash generated by the core business. More reliable than net income because it leaves out non-cash items.',
  'Capital expenditure':  'Cash spent buying or maintaining physical assets such as property, plant and equipment. Needed for growth.',
  'Free cash flow':       'Operating cash flow minus capital expenditure. The cash left for dividends, paying down debt or acquisitions.',
  'EBITDA margin':        'EBITDA divided by revenue. The share of revenue that becomes operating cash before financing costs.',
  'Net profit margin':    'Net income divided by revenue. Profitability after all costs.',
  'Return on equity (ROE)':            'Net income divided by shareholders’ equity. Above 15% is good.',
  'Return on assets (ROA)':            'Net income divided by total assets. Banks typically aim for 1% to 2%.',
  'Return on capital employed (ROCE)': 'Operating income divided by capital employed (assets minus current liabilities). A common measure of how well capital is used.',
  'Debt / Equity':        'Total debt divided by shareholders’ equity: higher means more debt-funded. For banks, net interest margin is shown instead.',
  'Net debt / EBITDA':    'Net debt divided by EBITDA, roughly the years of EBITDA needed to repay it. Above 4x is usually a concern.',
  'Interest coverage':    'EBITDA divided by interest expense, or return on assets for banks. Below 1.5x is risky.',
  'Net interest income':              'Interest earned on loans minus interest paid on deposits. A bank\'s main revenue line.',
  'Non-interest income':              'Fees, trading gains and other income that isn\'t lending. It reduces reliance on interest rates.',
  'Pre-provision operating profit':   'Net interest income plus non-interest income. Earnings before provisions for bad loans.',
  'Net interest margin (NIM)':        'Net interest income divided by total assets, in place of earning assets. Indian banks are typically at 3% to 4%.',
  'Cost-to-income ratio':             'Operating expenses divided by total income. Below 50% is efficient for an Indian bank.',
  'Deal value':           'What is paid for the stake: target market capitalisation × (1 + offer premium) × ownership acquired.',
  'Ownership acquired':   'The share of the target the acquirer ends up owning. It decides the accounting: below 20% an investment, 20% to 50% the equity method, above 50% consolidation.',
  'Less: non-controlling interest': 'The part of the target\'s net income, and of the net income synergy, that belongs to its other shareholders. The target is consolidated in full, so this is deducted before the acquirer\'s EPS.',
  'Net income attributable to owners': 'Consolidated net income less the non-controlling interest. The acquirer\'s EPS is computed on this figure.',
  'Non-controlling interest': 'The other shareholders\' share of the target\'s book equity. It sits within equity on a consolidated balance sheet.',
  'Cash component':       'The part of the deal value paid in cash. It is funded with new debt, so it adds interest cost but no new shares.',
  'Stock component':      'The part of the deal value paid in new acquirer shares. It needs no cash but dilutes existing shareholders.',
  'New shares issued':    'New acquirer shares created to pay the stock component.',
  'Share dilution':       'The percentage increase in shares outstanding. It reduces earnings per share in proportion.',
  'New annual interest cost': 'Interest on the new debt raised to pay the cash component, at an assumed 8%.',
  'Goodwill created':     'Deal value above the target\'s book equity. It is booked as an intangible asset and tested for impairment every year.',
  'EPS change':           'The change in the acquirer\'s earnings per share after the deal. A rise is called accretive and a fall is called dilutive.',
  'Cost synergies':       'Savings from removing overlap: headcount, branches, procurement and IT systems.',
  'Revenue synergies':    'Extra revenue from cross-selling, wider distribution or pricing power after the deal.',
  'Total synergy value':  'The annual value of cost and revenue synergies together. They usually take two to three years to arrive in full.',
  'Intrinsic value / share': 'Fair value per share from the valuation model (DCF, DDM or EV/EBITDA). Compare it with the share price to see whether the stock looks cheap or expensive.',
  'Enterprise value':     'The value of the whole business: equity value plus net debt. Roughly what it would cost to buy the company debt-free.',
  '(-) Net debt':         'Total debt minus cash including short-term investments, subtracted from enterprise value to reach equity value.',
  'Equity value':         'The value that belongs to shareholders: enterprise value minus net debt.',
  'Present value of free cash flows':           'Present value of the projected free cash flows over the forecast period, discounted at WACC.',
  'Present value of terminal value': 'Present value of all cash flows after the forecast period, usually 60% to 80% of the DCF value.',
  'Implied enterprise value': 'EBITDA × sector multiple: what the market would pay for this business at the median multiple for its sector.',
  'Implied equity value': 'Implied enterprise value minus net debt. The value that belongs to shareholders under the EV/EBITDA method.',
  'WACC':                 'Weighted average cost of capital: the blended return that debt and equity investors require. Used as the DCF discount rate.',
  'Free cash flow growth':           'The assumed yearly growth in free cash flow over the forecast period.',
  'Terminal growth':      'The growth rate assumed forever after the forecast period. It should not be above long-run GDP growth.',
  'Forecast period':      'Years of explicit cash flow forecasts before the terminal value.',
  'Cost of equity':       'The return equity investors require: risk-free rate + beta × equity risk premium.',
  'Dividend growth rate': 'The growth rate assumed forever for dividends in the Gordon Growth DDM.',
  'Dividend per share (current year)':   'Dividends per share paid in the latest financial year.',
  'Dividend per share (next year, estimated)': 'Estimated dividend for next year: dividend per share × (1 + dividend growth rate).',
  'EV/EBITDA multiple':   'The sector median of enterprise value to EBITDA. Used to value companies with negative free cash flow.',
  'Combined market capitalisation':   'Acquirer market capitalisation plus deal value: the implied equity value of the combined company.',
  'Combined total assets': 'Both companies\' total assets plus the goodwill created in the deal.',
  'Combined total debt':   'Both companies\' existing debt plus the new debt raised for the cash component.',
  'Combined cash':         'Both companies’ cash after the cash paid in the deal. Rs 0 means the cash paid exceeds cash held, so more debt is needed.',
  'Goodwill on books':     'Purchase price above the fair value of the net assets acquired. Booked as an intangible asset and tested for impairment every year.',
  'CAGR':                  'Compound annual growth rate: the average yearly growth over the years shown, using however many years of data are available.',
};

const GROUP_LABELS = {
  private_banks:'Private banks', psu_banks:'Public sector banks', nbfc_lending:'Lending NBFCs',
  housing_finance:'Housing finance', gold_loan_nbfc:'Gold loan NBFCs',
  infra_finance_psu:'Infrastructure finance (public sector)', life_insurance:'Life insurance',
  general_health_insurance:'General & health insurance',
  amc_capital_markets:'Asset managers & market infrastructure', it_services:'Information technology services',
  oil_gas_upstream:'Oil & gas, upstream', oil_gas_refining:'Oil & gas, refining & marketing',
  lubricants:'Lubricants', gas_utilities:'Gas utilities', power_utilities:'Power utilities',
  metals_steel:'Steel', metals_nonferrous:'Non-ferrous metals', mining:'Mining',
  fmcg:'FMCG', alcoholic_beverages:'Alcoholic beverages',
  consumer_durables:'Consumer durables', cables_wires:'Cables & wires', paints:'Paints',
  jewellery:'Jewellery', auto_oem_4w:'Passenger vehicle makers',
  auto_oem_2w:'Two-wheeler makers', auto_cv:'Commercial vehicles',
  auto_ancillary:'Automotive components', tyres:'Tyres', pharma:'Pharmaceuticals',
  hospitals:'Hospitals', diagnostics:'Diagnostics',
  capital_goods_defense:'Capital goods & defence', construction_epc:'Construction and engineering contracting',
  cement:'Cement', realty:'Real estate', specialty_chemicals:'Specialty chemicals',
  agri_fertilizers:'Agrochemicals & fertilisers', telecom:'Telecommunications operators',
  telecom_equipment:'Telecommunications equipment',
};
const groupLabel = (g) => GROUP_LABELS[g]
  || String(g || '').replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

const NA_REASONS = {
  'Operating income_financial':   'Operating income in the corporate sense doesn\'t apply to banks. Use pre-provision operating profit instead.',
  'EBITDA_financial':             'EBITDA isn\'t meaningful for a bank, because depreciation and interest can\'t be separated from its core business.',
  'EBITDA margin_financial':      'EBITDA margin doesn\'t apply to financial companies.',
  'Net debt / EBITDA_financial':  'Net debt/EBITDA doesn\'t apply: a bank holds debt as an asset (loans), not just as a liability.',
  'Free cash flow_negative':      'Free cash flow is negative: the company spends more on capital expenditure than its operations bring in. The EV/EBITDA model is used instead.',
  'Dividend yield_data':          'The dividend yield for some NSE stocks is unreliable. This value was outside a plausible range, so it was dropped.',
  'ROCE_missing':                 'The current liabilities figure is not available for this company, so ROCE can\'t be calculated.',
  'nonbank_in_mixed':             'This only applies to financial companies (banks, NBFCs and insurers).',
  'banking_data_unavailable':     'This company reports non-interest income inside total revenue rather than as its own line, so it isn\'t shown separately.',
  'bank_no_standard_is':  'Banks report income as net interest income, not as revenue, operating income or EBITDA. See the banking income statement for this company\'s figures.',
  'nonbank_no_banking':   'These banking metrics only apply to financial companies (banks, NBFCs and insurers).',
  'bank_no_margin':       'Margins are based on revenue, which banks don\'t report in the corporate sense. NIM and ROA are the banking equivalents.',
  'bank_no_roce':         'ROCE uses operating income and capital employed, which don\'t apply to banks. ROA and ROE are used instead.',
  'debt_equity_bank':      'A bank\'s largest liability is deposits, which fund the business rather than lever it. NIM is the banking equivalent.',
  'net_debt_ebitda_bank':  'Banks don\'t report EBITDA in the corporate sense, and they hold debt as an earning asset. ROA is used instead.',
  'interest_coverage_bank':'For a bank, paying interest on deposits is the business, not a solvency risk. NIM is the banking equivalent.',
  'nim_missing':          'NIM can\'t be computed because the figures it needs aren\'t available for this company.',
  'Cost-to-Income_financial': 'The cost-to-income ratio needs operating expenses and total operating income split out cleanly, and that isn\'t available for this company.',
  'cagr_nm':              'This growth rate isn\'t meaningful: the starting year was unusually small or volatile, which distorts the percentage. Use the yearly figures instead.',
  'cagr_insufficient_years': 'A growth rate needs at least two years of data. Only one year is available once both companies are aligned to the same period.',
  'cagr_negative_base':      'A compound growth rate can\'t be computed from a negative starting value. Use the yearly figures instead.',
  'cagr_zero_base':          'A compound growth rate can\'t be computed from a starting value of zero. Use the yearly figures instead.',
  'cagr_negative_recent':    'This figure is negative in the latest period, so a positive growth rate would mislead. The yearly figures show the real trend.',
  'cagr_capped_nm':          'The starting year was unusually small or volatile, so a compound rate comes out extreme. Use the yearly figures instead.',
  'ltm_unavailable':         'Quarterly data for the last twelve months is incomplete for this company, so there is no LTM figure. Use the annual figure instead.',
  'oi_gt_ebitda':            'Not shown: the source reports operating income above EBITDA, which cannot be right. EBITDA and net income are unaffected.',
  'ltm_not_core_metric':     'LTM is shown only for revenue, EBITDA and net income, or the bank equivalents. This metric is annual only.',
  'comps_multiple_unavailable':'This multiple isn\'t available for this company.',
  'comps_nm_negative_base': 'Not meaningful: the base of this multiple (EBITDA, net earnings or book equity) is zero or negative for this company.',
  'comps_nm_negative_ev': 'Not meaningful: net cash exceeds market capitalisation, so enterprise value is negative.',
  'comps_nm_extreme': 'Not meaningful: the base of this multiple is so close to zero that the ratio runs into hundreds or thousands. The arithmetic is right, but it says nothing about value.',
  'comps_outlier': 'The company\'s actual multiple. Far outside the useful range, so it is left out of the median, mean and range.',
  'comps_nr': 'Not reported for this company.',
  'negative_ebitda_coverage': 'Not meaningful: EBITDA is negative, so there is no operating profit to cover interest.',
  'negative_ebitda_leverage': 'Not meaningful: EBITDA is zero or negative, so net debt divided by it is not a number of years of earnings. The net debt figure itself is on the balance sheet above.',
  'negative_capital_employed': 'Not meaningful: current liabilities exceed total assets, so capital employed is negative. A return measured against it inverts sign.',
  'negative_equity': "Not meaningful: book equity is negative, so a ratio against it changes sign rather than size. The balance sheet above carries the figures.",
  'comps_fx_unpinned': 'Left out: this company reports in another currency and the period exchange rates couldn\'t be fetched. Today\'s rate would move the peer median.',
  'comps_broad_match': 'No median: these peers come from a broad sector match, not a curated group. Edit the peer list and the statistics will appear.',
  'nii_not_reported':      'Net interest income isn\'t reported as its own line for this company. Total revenue isn\'t used in its place, because it includes fee, treasury and other income and would overstate the interest margin.',
  // P4c. THIS SAID THE BANK DOES NOT REPORT THE FIGURE, which this data cannot show.
  // Every Indian bank reports fee and other income in its filings; what is true is that
  // it is not in the data this site reads - 0 of the 26 banks and lenders in the frozen
  // store carry it, measured 7 October 2026.
  'non_ii_not_reported':   'Fee and other income is not available in Dealquire\'s data for this company.',
  'ppop_partial':          'Shown as net interest income alone, because fee and other income is not available in Dealquire\'s data. Full pre-provision operating profit would add it.',
  'cash_split_unavailable': 'The data source doesn\'t split this company\'s cash into cash equivalents and short-term investments in a way that adds up, so the split isn\'t shown. Cash including short-term investments, below, is the figure net debt uses, and it is unaffected.',
  'sti_not_in_cash':        'This company reports no short-term investments, so the cash deducted in net debt is cash and cash equivalents alone.',
  // Same codes and wording as OWNERSHIP_SUPPRESSION_REASONS in backend/app.py.
  // THE TWO OWNERSHIP-SUPPRESSION REASONS ARE NOT HERE. They were written out twice, once
  // in OWNERSHIP_SUPPRESSION_REASONS in app.py and once here, and a copy that has to be
  // kept in step is a copy that will not be. app.py is the source: it already sends the
  // text with the payload, as proforma.suppression_reasons[code] and as
  // deal_characteristics.suppressed_reason, and every surface now reads that.
};

// ---- INDIAN NUMBER FORMAT: THE ONE MONEY FORMATTER ----------------------------------
// Amounts in Rs crore with a "cr" suffix; Indian digit grouping (99,053 / 3,26,343 /
// 17,01,708 - the last three digits, then pairs); two decimals only under 100 cr;
// negative as -Rs 25,400 cr. Per-share prices go through fmtRs, in rupees with two
// decimals. The backend's fmt_cr and the workbook's NF_CR implement the same rules -
// change all three together. It replaced T/B/Cr suffixes, which read as a foreign tool
// ("Rs 3.26T" for HCLTECH's market cap). Display only: the payload stays in rupees.
const groupIndian = (digits) => {
  if (digits.length <= 3) return digits;
  let head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const pairs = [];
  while (head.length > 2) { pairs.unshift(head.slice(-2)); head = head.slice(0, -2); }
  if (head) pairs.unshift(head);
  return `${pairs.join(',')},${tail}`;
};
const fmtIndianAbs = (abs, dp) => {
  const [ip, fp] = abs.toFixed(dp).split('.');
  return fp === undefined ? groupIndian(ip) : `${groupIndian(ip)}.${fp}`;
};
// The rounding rule, kept free of JSX so it can be exercised on its own. A NON-ZERO amount
// never reads as zero: anything under Rs 50,000 used to round to "Rs 0.00 cr", and now
// reads "< Rs 0.01 cr" / "> -Rs 0.01 cr" - a bound in the SAME unit as the rest of the
// column, not a switch to lakh or rupees mid-table. Exact zero still reads "Rs 0.00 cr".
// fmt_cr (Python) and the Excel NF_CR_TINY conditional format implement the same rule.
const fmtCrParts = (num) => {
  if (num === null || num === undefined || Number.isNaN(Number(num))) return { text: 'N/A', bound: false };
  const n    = Number(num);
  const cr   = n / 1e7;
  const abs  = Math.abs(cr);
  const body = fmtIndianAbs(abs, Number(abs.toFixed(2)) < 100 ? 2 : 0);
  const zeroBody = Number(body.replace(/,/g, '')) === 0;
  if (zeroBody && n !== 0) {
    const ra = Math.abs(n);
    return { text: n > 0 ? '< Rs 0.01 cr' : '> -Rs 0.01 cr', bound: true,
             exact: `${n < 0 ? '-' : ''}Rs ${fmtIndianAbs(ra, ra < 1 ? 2 : 0)}` };
  }
  return { text: `${cr < 0 && !zeroBody ? '-' : ''}Rs ${body} cr`, bound: false };
};
// Returns a string, except for a bound, which carries the exact figure in a tooltip - so
// fmt() must be rendered as a JSX child, never interpolated into a template string.
const fmt = (num) => {
  const p = fmtCrParts(num);
  if (!p.bound) return p.text;
  return (
    <Tooltip text={`Exact figure: ${p.exact}. It is below Rs 0.01 cr, so it would otherwise round to Rs 0.00 cr.`}>
      {p.text}
    </Tooltip>
  );
};
// A per-share price in rupees. A non-zero price never reads as zero: below Rs 0.005 it reads
// "< Rs 0.01" / "> -Rs 0.01", and exact zero reads "Rs 0.00" - fmtCrParts' rule, per share.
// fmt_rs (Python) and the Excel NF_RS_TINY conditional format implement the same rule.
// An input percentage, printed as entered: 20%, 20.5%, 20.25%. Mirrors fmt_pct_input in
// app.py and the Excel number format '0.##"%"'. A computed ratio keeps its fixed decimals;
// an input should not carry digits the user never typed.
const fmtPctInput = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 'n.a.';
  return `${Number(Number(v).toFixed(2))}%`;
};

const fmtRs = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 'N/A';
  const n    = Number(v);
  const body = fmtIndianAbs(Math.abs(n), 2);
  const zeroBody = Number(body.replace(/,/g, '')) === 0;
  if (zeroBody && n !== 0) return n > 0 ? '< Rs 0.01' : '> -Rs 0.01';
  return `${n < 0 && !zeroBody ? '-' : ''}Rs ${body}`;
};
const fmtN        = (n, dec=2) => (n !== null && n !== undefined) ? Number(n).toFixed(dec) : 'N/A';
const fmtRatioPct = (n) => (n !== null && n !== undefined) ? `${(n*100).toFixed(1)}%` : 'N/A';
const fmtMultiple = (n) => (n !== null && n !== undefined) ? `${Number(n).toFixed(2)}x` : 'N/A';
// A SIGNED PERCENTAGE, for a figure that is already a percentage and can be negative.
// fmtPct below takes a 0-to-1 ratio and returns 'N/A' for anything negative, which is right for
// a margin and wrong for "against the price": it printed N/A for every value below the market.
const fmtSignedPct = (v, dp = 1) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 'N/A';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(dp)}%`;
};

const fmtPct = (n) => {
  if (n === null || n === undefined) return 'N/A';
  let pct = n * 100;
  if (pct > 25) pct = n;
  if (pct > 25 || pct < 0) return 'N/A';
  return `${pct.toFixed(2)}%`;
};
const fmtCagr = (obj) => {
  if (!obj || obj.cagr === null || obj.cagr === undefined) return 'N/A';
  if (obj.reason === 'capped_nm') return 'n.m.';
  const pct = obj.cagr * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}% (${obj.years}-yr)`;
};

// WHICH N/A REASONS HAVE A TOPIC TO READ. Keyed by NA_REASONS key; the lookup below inverts it
// on the TEXT, so a call site that already passes NA_REASONS[key] gets the link without changing.
// A reason with no entry here simply has no link - the sentence is the whole answer for it.
const NA_REASON_TOPICS = {
  'ltm_unavailable': 'ltm',
  'ltm_not_core_metric': 'ltm',
  'oi_gt_ebitda': 'ebitda-composition',
  'Operating income_financial': 'ebitda-composition',
  'EBITDA_financial': 'ebitda-composition',
  'EBITDA margin_financial': 'ebitda-composition',
  'Free cash flow_negative': 'dcf',
  'comps_multiple_unavailable': 'peer-selection',
  'comps_nm_negative_base': 'peer-selection',
  'comps_nm_negative_ev': 'peer-selection',
  'comps_nm_extreme': 'peer-selection',
  'comps_outlier': 'peer-selection',
  'comps_nr': 'peer-selection',
  'comps_broad_match': 'peer-selection',
  'comps_fx_unpinned': 'currency-translation',
  'negative_ebitda_coverage': 'credit-impact',
  'negative_ebitda_leverage': 'credit-impact',
  'net_debt_ebitda_bank': 'credit-impact',
  'interest_coverage_bank': 'credit-impact',
  'debt_equity_bank': 'credit-impact',
  'negative_capital_employed': 'credit-impact',
  'negative_equity': 'credit-impact',
  'cash_split_unavailable': 'net-debt',
  'sti_not_in_cash': 'net-debt',
};
const NA_TOPIC_BY_TEXT = new Map(
  Object.entries(NA_REASON_TOPICS)
    .filter(([k]) => NA_REASONS[k])
    .map(([k, topic]) => [NA_REASONS[k], topic])
);

function Tooltip({ text, children, naReason, naLabel, naTopic }) {
  const [pos, setPos]         = useState(null);
  const [visible, setVisible] = useState(false);
  // PINNED = opened by click or tap. It is the only way to read a reason on a phone, where
  // mouseenter never fires, and the only way to click a link inside one anywhere.
  const [pinned, setPinned]   = useState(false);
  const show = useCallback((e) => { setPos({ x: e.clientX, y: e.clientY }); setVisible(true); }, []);
  const move = useCallback((e) => { setPos({ x: e.clientX, y: e.clientY }); }, []);
  const hide = useCallback(() => setVisible(false), []);
  const hasNa  = !!(naReason && naReason.trim());
  const topic  = naTopic || (hasNa ? NA_TOPIC_BY_TEXT.get(naReason) : null);
  const toggle = useCallback((e) => {
    e.stopPropagation(); e.preventDefault(); setVisible(false); setPinned(p => !p);
  }, []);
  return (
    <>
      <span onMouseEnter={show} onMouseMove={move} onMouseLeave={hide} style={{ cursor:'help' }}>
        {children}
        {hasNa && (
          <button type="button" onClick={toggle} aria-expanded={pinned}
                  aria-label={`${pinned ? 'Hide' : 'Show'} why this is not available`}
                  style={{ marginLeft:'5px', padding:0, border:'none', background:'none',
                           color:C.info, fontSize:S.micro, fontWeight:W.bold, cursor:'pointer',
                           fontFamily:F.ui, verticalAlign:'baseline' }}>ⓘ</button>
        )}
      </span>
      {hasNa && pinned && (
        <span role="note" style={{ display:'block', margin:`${SP.sm} 0`, padding:`${SP.sm} ${SP.md}`,
                                   borderLeft:`3px solid ${C.info}`, backgroundColor:C.surfaceAlt,
                                   borderRadius:R.sm, color:C.textMuted, fontSize:S.small,
                                   lineHeight:1.6, textAlign:'left', fontWeight:W.normal }}>
          <span style={{ color:C.info, fontWeight:W.bold }}>{naLabel || 'Why N/A?'}</span>{' '}
          {naReason}{' '}
          {topic && (
            <a href={`${METHODOLOGY_PATH}#${topic}`} target="_blank" rel="noopener noreferrer"
               style={{ color:C.accent, fontWeight:W.semi, textDecoration:'none', whiteSpace:'nowrap' }}>
              Read the method &#8599;
            </a>
          )}{' '}
          <button type="button" onClick={toggle}
                  style={{ padding:0, border:'none', background:'none', color:C.textFaint,
                           fontSize:S.micro, cursor:'pointer', fontFamily:F.ui }}>Close</button>
        </span>
      )}
      {visible && pos && !pinned && (
        <div style={{
          position:'fixed', left:Math.min(pos.x+16, window.innerWidth-310), top:pos.y+16,
          zIndex:9999, backgroundColor:C.surfaceAlt, border:`1px solid ${C.line}`, borderRadius:R.md,
          padding:'12px 16px', maxWidth:'280px', minWidth:'160px',
          pointerEvents:'none', boxShadow:'0 6px 24px rgba(0,0,0,0.7)',
        }}>
          {naReason && naReason.trim() ? (
            <>
              <p style={{ color:C.info, fontSize:S.small, fontWeight:W.bold, margin:'0 0 6px 0' }}>{naLabel || 'Why N/A?'}</p>
              <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:'1.6', margin:0 }}>{naReason}</p>
            </>
          ) : text ? (
            <p style={{ color:C.text, fontSize:S.small, lineHeight:'1.6', margin:0 }}>{text}</p>
          ) : null}
        </div>
      )}
    </>
  );
}

// EVERY ROW LABEL WHOSE SUBJECT IS A TOPIC, and the topic it opens. The tooltip stays a
// one-liner: this adds a LINK to the full explanation rather than restating it. A topic that
// is still a stub links to its heading on the methodology page, which is honest about there
// being nothing written yet; InfoMarker renders that case as a plain link with no panel.
const LABEL_TOPICS = {
  // Reading the financial statements
  'EBITDA': 'ebitda-composition',
  'EBITDA margin': 'ebitda-composition',
  'Net debt': 'net-debt',
  '(-) Net debt': 'net-debt',
  'Cash including short-term investments': 'net-debt',
  'Restricted cash, reported but not deducted': 'net-debt',
  // Valuation
  'Intrinsic value / share': 'valuation-method-selection',
  'Present value of free cash flows': 'dcf',
  'Present value of terminal value': 'dcf',
  'Free cash flow growth': 'dcf',
  'Terminal growth': 'dcf',
  'Forecast period': 'dcf',
  'WACC': 'wacc',
  'Cost of equity': 'wacc',
  'Beta': 'beta',
  'Dividend growth rate': 'ddm',
  'Dividend per share (current year)': 'ddm',
  'Dividend per share (next year, estimated)': 'ddm',
  'EV/EBITDA multiple': 'ev-ebitda-selection',
  'Implied enterprise value': 'ev-ebitda-selection',
  'Implied equity value': 'ev-ebitda-selection',
  // Deal mechanics
  'EPS change': 'accretion-mechanics',
  'Share dilution': 'accretion-mechanics',
  'Cost synergies': 'synergies',
  'Revenue synergies': 'synergies',
  'Total synergy value': 'synergies',
  'Goodwill created': 'goodwill-ppa',
  'Goodwill on books': 'goodwill-ppa',
  'Ownership acquired': 'nci-ownership',
  'Non-controlling interest': 'nci-ownership',
  'Less: non-controlling interest': 'nci-ownership',
  'Net income attributable to owners': 'nci-ownership',
  'Net debt / EBITDA': 'credit-impact',
  'Interest coverage': 'credit-impact',
  // Comparable companies
  'P/E ratio': 'pe-basis',
  'P/B ratio': 'pe-basis',
};

// One label, everywhere: its tooltip, and a link to its topic when it has one. Before
// 20-Sep-2026 seven call sites wrote the same Tooltip by hand and only two of them could
// carry a marker.
function MetricLabel({ label, defKey }) {
  const key = defKey || label;
  return (
    <>
      <Tooltip text={METRIC_DEFINITIONS[key]}>{label}</Tooltip>
      {LABEL_TOPICS[key] && <InfoMarker id={LABEL_TOPICS[key]} />}
    </>
  );
}

function MetricTable({ metrics, c1, c2, naReasons = {} }) {
  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', padding:'12px 20px', backgroundColor:C.surfaceAlt, borderBottom:`1px solid ${C.line}` }}>
        <span style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi }}>Metric</span>
        <span style={{ color:C.textLabel, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{c1.ticker}</span>
        <span style={{ color:C.textLabel, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{c2.ticker}</span>
      </div>
      {metrics.map(([label, v1, v2]) => {
        const naKey1 = naReasons[`${label}_c1`];
        const naKey2 = naReasons[`${label}_c2`];
        // A BARE 'N/A' COUNTS TOO. fmtRatioPct and fmtMultiple return exactly that for a
        // null figure, so a cell suppressed by the backend - ROE and Debt / Equity on negative
        // book equity, for instance - fell through this test and rendered with no tooltip and
        // no reason. A cell with no reason key is unchanged: Tooltip with naReason undefined
        // renders its children and nothing more.
        const isNA   = (v) => v === 'N/A' || v === 'N/A (non-bank)' || v === 'N/A (bank)' || v === 'N/A: see note';
        return (
          <div key={label} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
            <span style={{ color:C.textMuted, fontSize:S.body }}>
              <MetricLabel label={label} />
            </span>
            <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>
              {isNA(v1)
                ? <Tooltip naReason={naKey1 ? NA_REASONS[naKey1] : undefined}><span style={{ color:C.textFaint }}>{v1}</span></Tooltip>
                : v1}
            </span>
            <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>
              {isNA(v2)
                ? <Tooltip naReason={naKey2 ? NA_REASONS[naKey2] : undefined}><span style={{ color:C.textFaint }}>{v2}</span></Tooltip>
                : v2}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---- CASH BUILD-UP ----------------------------------------------------------------
// The cash figure net debt deducts is BROAD: cash, equivalents AND short-term
// investments. These cells show what it is built from, so a reader cross-checking
// against a "Cash & Bank" source can see where a difference comes from. Visibility only
// - net debt itself is computed server-side and is not re-derived here.
const ceCell = (c) => {
  const s = c.cash_split_status;
  if (s === 'split' || s === 'no_short_term_investments_row') return fmt(c.cash_and_equivalents);
  return 'N/A: see note';
};
const stiCell = (c) => (c.cash_split_status === 'split')
  ? fmt(c.short_term_investments)
  : 'N/A: see note';
// Restricted cash is DISPLAYED, never deducted. A company with no restricted-cash row shows
// nothing - an empty cell, not a zero that would read as a measured figure.
const rcCell = (c) => (c.restricted_cash !== null && c.restricted_cash !== undefined) ? fmt(c.restricted_cash) : '';
const cashSplitNaReasons = (c1, c2) => {
  const out = {};
  [[c1, 'c1'], [c2, 'c2']].forEach(([c, k]) => {
    const s = c.cash_split_status;
    if (s === 'split') return;
    if (s === 'no_short_term_investments_row') { out[`Short-term investments_${k}`] = 'sti_not_in_cash'; return; }
    out[`Cash & cash equivalents_${k}`] = 'cash_split_unavailable';
    out[`Short-term investments_${k}`]  = 'cash_split_unavailable';
  });
  return out;
};

// The short-term-investments share of a company's cash figure, as a plain statement. It is
// stated for every company that reports them, whatever the share: a warning above 25% fired
// for every company tested, so it told the reader nothing. A string, so it can be checked alone.
const stiShareSentence = (c) => {
  if (!c) return null;
  if (c.cash_split_status === 'no_short_term_investments_row')
    return `${c.ticker} reports no short-term investments, so its cash figure is cash and cash equivalents alone.`;
  if (c.cash_split_status !== 'split' || c.sti_share_of_cash === null || c.sti_share_of_cash === undefined) return null;
  return `${c.ticker}: short-term investments are ${fmtCrParts(c.short_term_investments).text}, ${Math.round(c.sti_share_of_cash * 100)}% of its cash figure.`;
};

// The explanation of net debt lives in explanations.py ('net-debt'); the marker on the
// Net debt row opens it. What stays here is this deal's own figures: how much of each
// company's cash figure is short-term investments.
function NetDebtNote({ c1, c2 }) {
  const stiLines = [c1, c2].map(stiShareSentence).filter(Boolean);
  if (!stiLines.length) return null;
  return (
    <p style={{ color:C.textFaint, fontSize:S.small, marginTop:`-${SP.xxs}`, marginBottom:SP.lg, lineHeight:1.5 }}>
      {stiLines.join(' ')}
    </p>
  );
}

// ---- FX TRANSLATION BASIS ---------------------------------------------------------
// A company whose statements the data source serves in another currency carries the
// rates it was translated at, stated beside the figures. Period rates are pinned to the
// statement period; a spot fallback is said to be unpinned in as many words.
// EXPLICIT MONTH NAMES, NOT THE RUNTIME'S. toLocaleDateString('en-GB', {month:'short'})
// renders September as "Sept" on ICU 78.3 and August as "Aug", so the SAST turnover window
// read "01 Sept 2025 to 31 Aug 2026" - two abbreviation lengths in one line, and output that
// changes with the browser's ICU version and locale. These twelve entries are the same ones
// app.py's MONTHS_ABBR carries, so the web, the tearsheet and the workbook agree.
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDateObj = (d) => (d && !isNaN(d.getTime()))
  ? `${String(d.getDate()).padStart(2, '0')} ${MONTHS_ABBR[d.getMonth()]} ${d.getFullYear()}`
  : '?';
// Accepts a date-only string ('2025-09-01') or a full timestamp; a bare date is read at
// local midnight, as it was before, so no date shifts by a day.
const fmtDate = (s) => s
  ? fmtDateObj(new Date(/[T ]/.test(String(s)) ? String(s) : `${s}T00:00:00`))
  : '?';
// Earlier years and LTM each carry their own rates (historical_fx / ltm_fx); the note
// names them so any figure in the historical table can be traced to the rate behind it.
const fxHistorySentence = (c) => {
  const hf = c.historical_fx;
  if (!hf || !Array.isArray(hf.periods)) return null;
  const earlier = hf.periods.filter(p => p.index > 0 && p.income_average != null);
  const parts = [];
  if (earlier.length) {
    parts.push(` Earlier years use their own rates, not today's: ${earlier.map(p => `${p.fy_label || 'FY'} average Rs ${fmtN(p.income_average, 2)}`).join('; ')}. Each year's balance sheet uses that year's closing rate.`);
  }
  const omitted = (hf.omitted || []).length;
  if (omitted) parts.push(` ${omitted} historical figure${omitted === 1 ? ' is' : 's are'} not shown because there are no exchange rates for ${omitted === 1 ? 'its' : 'their'} period.`);
  const lf = c.ltm_fx;
  if (lf && lf.basis === 'ltm_period_average') {
    parts.push(` LTM figures use the average rate over their four quarters, ${fmtDate(lf.period_start)} to ${fmtDate(lf.period_end)}: Rs ${fmtN(lf.average, 2)}.`);
  } else if (lf && lf.basis === 'omitted') {
    parts.push(' LTM figures are not shown because there are no exchange rates for the four quarters they cover.');
  }
  return parts.join('');
};

function FxBasisNote({ c1, c2 }) {
  const converted = [c1, c2].filter(c => c && c.fx_conversion_applied);
  if (!converted.length) return null;
  // Rendered BELOW the figures on each tab - a caveat is read after the number it
  // qualifies, not as five lines standing between the reader and the first figure.
  return (
    <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'12px 16px', border:`1px solid ${C.lineSoft}`, marginTop:SP.lg, marginBottom:SP.lg }}>
      {converted.map(c => (
        <p key={c.ticker} style={{ color:C.textFaint, fontSize:S.small, lineHeight:'1.5', margin:'0 0 4px' }}>
          {c.fx_basis === 'spot_fallback' ? (
            <><strong style={{ color:C.target }}>{c.ticker}: translated at today's spot rate of Rs {fmtN(c.fx_rate_income, 4)} per {c.financial_currency}, not the rate for the reporting period.</strong>{' '}
              The data source gives this company's statements in {c.financial_currency} and the rates for the period couldn't be fetched. These figures will move with the exchange rate, so treat them as approximate.<InfoMarker id="currency-translation" /></>
          ) : (
            <><strong style={{ color:C.textMuted }}>{c.ticker}: translated from {c.financial_currency}.</strong>{' '}
              For the latest year, the income statement and cash flow use the average of Rs {fmtN(c.fx_rate_income, 4)} ({c.fx_average_observations} daily closes,
              {' '}{fmtDate(c.fx_period_start)} to {fmtDate(c.fx_period_end)}), and the balance sheet uses the closing rate of Rs {fmtN(c.fx_rate_balance, 4)} ({fmtDate(c.fx_closing_date)}).
              {fxHistorySentence(c)}<InfoMarker id="currency-translation" /></>
          )}
        </p>
      ))}
    </div>
  );
}

// A ONE-OFF INSIDE REPORTED EBITDA IS FLAGGED, NEVER SUBSTITUTED. The backend measures the
// share and sets one_off_material; nothing is restated, because the data source reports an
// unusual item for 94% of companies and in almost every year, which is a standing adjustment
// rather than the exceptional item a company disclosed (app.py, ONE_OFF_MATERIAL_PCT).
function OneOffNote({ c1, c2 }) {
  const flagged = [c1, c2].filter(c => c && c.one_off_material);
  if (!flagged.length) return null;
  return (
    <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'12px 16px', border:`1px solid ${C.lineSoft}`, marginTop:SP.lg, marginBottom:SP.lg }}>
      {flagged.map(c => (
        <p key={c.ticker} style={{ color:C.textFaint, fontSize:S.small, lineHeight:'1.5', margin:'0 0 4px' }}>
          <strong style={{ color:C.target }}>{c.ticker}: {c.unusual_items_pct_of_ebitda}% of reported EBITDA is a one-off.</strong>{' '}
          The data source reports unusual items of {fmt(c.unusual_items)} inside EBITDA of {fmt(c.ebitda)}.
          Every EBITDA-derived figure carries it, and nothing here is restated.
          Excluding the item the source reports {fmt(c.normalized_ebitda)}.
        </p>
      ))}
    </div>
  );
}

const LTM_TOOLTIP_TEXT = 'Last twelve months: the last full fiscal year, plus the quarters since it, less the same quarters a year earlier. For display only; valuation uses annual figures.';

function IncomeStatementTable({ c1, c2, naReasons = {}, metrics }) {
  const gridCols = '2fr 1fr 0.7fr 1fr 0.7fr';
  const ltmCell = (c, v, ltmKey) => {
    const isNA = v === 'N/A (non-bank)' || v === 'N/A (bank)' || v === 'N/A: see note';
    if (isNA || !ltmKey) {
      return <Tooltip naReason={NA_REASONS['ltm_not_core_metric']}><span style={{ color:C.textFaint }}>—</span></Tooltip>;
    }
    const val = (c.ltm || {})[ltmKey];
    if (val === null || val === undefined) {
      // THE REASON, NOT A REASON. The backend records why each LTM figure is missing - the
      // quarter the source skips, or a quarterly EBITDA row that is really EBIT - and that
      // sentence names this company's own dates. NA_REASONS carries the generic fallback for
      // the cases it has not classified.
      const why = ((c.ltm || {}).reasons || {})[ltmKey] || NA_REASONS['ltm_unavailable'];
      // The reason is this company's own sentence rather than an NA_REASONS entry, so the topic
      // is named here instead of being looked up from the text.
      return <Tooltip naReason={why} naTopic="ltm"><span style={{ color:C.textFaint }}>N/A</span></Tooltip>;
    }
    return fmt(val);
  };
  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.sm }}>
      <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', backgroundColor:C.surfaceAlt, borderBottom:`1px solid ${C.line}` }}>
        <span style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi }}>Metric</span>
        <span style={{ color:C.textLabel, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{c1.ticker}</span>
        {/* 2f: WHICH twelve months. The window is the last full fiscal year plus the year to
            date less the prior year to date, so it ends at the newest quarter - which is not the
            same month for every company, and was not stated anywhere before. */}
        <span style={{ color:'#5fb8a8', fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>
          <Tooltip text={LTM_TOOLTIP_TEXT}>LTM</Tooltip>
          {(c1.ltm || {}).period_label && (
            <span style={{ display:'block', color:C.textFaint, fontSize:S.micro, fontWeight:W.normal }}>
              {(c1.ltm.period_label || '').replace(/^LTM /, '')}
            </span>
          )}
        </span>
        <span style={{ color:C.textLabel, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{c2.ticker}</span>
        <span style={{ color:'#5fb8a8', fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>
          <Tooltip text={LTM_TOOLTIP_TEXT}>LTM</Tooltip>
          {(c2.ltm || {}).period_label && (
            <span style={{ display:'block', color:C.textFaint, fontSize:S.micro, fontWeight:W.normal }}>
              {(c2.ltm.period_label || '').replace(/^LTM /, '')}
            </span>
          )}
        </span>
      </div>
      {metrics.map(([label, v1, v2, ltmKey]) => {
        const naKey1 = naReasons[`${label}_c1`];
        const naKey2 = naReasons[`${label}_c2`];
        const isNA   = (v) => v === 'N/A' || v === 'N/A (non-bank)' || v === 'N/A (bank)' || v === 'N/A (insurer)' || v === 'N/A: see note';
        return (
          <div key={label} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
            <span style={{ color:C.textMuted, fontSize:S.body }}>
              <MetricLabel label={label} />
            </span>
            <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>
              {isNA(v1)
                ? <Tooltip naReason={naKey1 ? NA_REASONS[naKey1] : undefined}><span style={{ color:C.textFaint }}>{v1}</span></Tooltip>
                : v1}
            </span>
            <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>{ltmCell(c1, v1, ltmKey)}</span>
            <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>
              {isNA(v2)
                ? <Tooltip naReason={naKey2 ? NA_REASONS[naKey2] : undefined}><span style={{ color:C.textFaint }}>{v2}</span></Tooltip>
                : v2}
            </span>
            <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>{ltmCell(c2, v2, ltmKey)}</span>
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ text }) {
  return <h3 style={{ color:C.textLabel, fontSize:S.h3, margin:'24px 0 10px', borderBottom:`1px solid ${C.lineSoft}`, paddingBottom:SP.sm }}>{text}</h3>;
}
function SubLabel({ text }) {
  return (
    <div style={{
      color: '#9aa3ad',
      fontSize: S.small,
      fontWeight: W.bold,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      padding: '14px 4px 8px',
      marginTop: SP.xs,
    }}>
      {text}
    </div>
  );
}

const NONFIN_HIST_ROWS = ['Revenue', 'EBITDA', 'Net income', 'Total assets', 'Free cash flow'];
const FIN_HIST_ROWS    = ['Net interest income', 'Pre-provision operating profit', 'Net income', 'Total assets'];
const MIXED_HIST_ROWS  = ['Revenue', 'EBITDA', 'Net interest income', 'Pre-provision operating profit', 'Net income', 'Total assets', 'Free cash flow'];
// B1. AN INSURER'S HISTORICAL ROWS. The bank set above was being used for insurers too, so the
// Historical Trend table printed three years of net interest income for a company that has none
// - at -Rs 94 cr, -Rs 92 cr and -Rs 91 cr - and a pre-provision operating profit built on it.
const INSURER_HIST_ROWS = ['Revenue', 'Net claims and benefits', 'Net income', 'Total assets'];
const MIXED_INSURER_HIST_ROWS = ['Revenue', 'Net claims and benefits', 'EBITDA', 'Net income', 'Total assets', 'Free cash flow'];
const HIST_META = {
  'Revenue':                          { key: 'revenue',              type: 'nonbank' },
  'EBITDA':                           { key: 'ebitda',               type: 'nonbank' },
  'Net income':                       { key: 'net_income',           type: 'both' },
  'Total assets':                     { key: 'total_assets',         type: 'both' },
  'Free cash flow':                   { key: 'free_cashflow',        type: 'nonbank' },
  'Net interest income':              { key: 'net_interest_income',  type: 'bank' },
  'Pre-provision operating profit':   { key: 'ppop',                 type: 'bank' },
  // B1. The insurer's own line, so the Historical Trend table has something to show it that is
  // not a bank measure. 'Revenue' is already above and an insurer reports one.
  'Net claims and benefits':          { key: 'policyholder_benefits', type: 'insurer' },
};

function HistoricalSubTable({ company, rowLabels }) {
  const hist = company.historical || {};
  const hasData = Object.keys(hist).length > 0;
  const fallbackLabels = ['FY-2', 'FY-1', 'FY (latest)'];
  const yearLabels = hist.fy_labels ? [...hist.fy_labels].reverse() : fallbackLabels;
  const gridCols = `2fr ${yearLabels.map(() => '1fr').join(' ')} 1.2fr`;
  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
      <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', backgroundColor:C.surfaceAlt, borderBottom:`1px solid ${C.line}` }}>
        <span style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi }}>Metric</span>
        {yearLabels.map((yl, i) => (
          <span key={i} style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{yl}</span>
        ))}
        <span style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>CAGR</span>
      </div>
      {!hasData ? (
        <div style={{ padding:'12px 20px' }}>
          <p style={{ color:C.textFaint, fontSize:S.body, margin:0 }}>No historical data for this company.</p>
        </div>
      ) : rowLabels.map(label => {
        const meta = HIST_META[label];
        const applicable = meta.type === 'both'
          || (meta.type === 'nonbank' && !company.is_financial)
          || (meta.type === 'bank' && company.is_financial);

        if (!applicable) {
          const naVal    = company.is_financial ? 'N/A (bank)' : 'N/A (non-bank)';
          const naReason = company.is_financial ? NA_REASONS['bank_no_standard_is'] : NA_REASONS['nonbank_no_banking'];
          return (
            <div key={label} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
              <span style={{ color:C.textMuted, fontSize:S.body }}><MetricLabel label={label} /></span>
              {yearLabels.map((_, i) => (
                <span key={i} style={{ textAlign:'right' }}>
                  <Tooltip naReason={naReason}><span style={{ color:C.textFaint, fontSize:S.body }}>{naVal}</span></Tooltip>
                </span>
              ))}
              <span style={{ textAlign:'right' }}>
                <Tooltip naReason={naReason}><span style={{ color:C.textFaint, fontSize:S.body }}>{naVal}</span></Tooltip>
              </span>
            </div>
          );
        }

        const entry   = hist[meta.key];
        const series  = entry?.series || [];
        const display = [...series].reverse();
        const cagrText = fmtCagr(entry);
        const reason   = entry?.reason;

        let cagrColor, cagrCell;
        if (entry && entry.cagr !== null && entry.cagr !== undefined && reason === 'capped_nm') {
          cagrColor = '#7a828c';
          cagrCell  = <Tooltip naReason={NA_REASONS['cagr_capped_nm']}>{cagrText}</Tooltip>;
        } else if (!entry || entry.cagr === null || entry.cagr === undefined) {
          cagrColor = C.textMuted;
          const naKey = reason ? `cagr_${reason}` : null;
          cagrCell  = (naKey && NA_REASONS[naKey])
            ? <Tooltip naReason={NA_REASONS[naKey]}>{cagrText}</Tooltip>
            : <Tooltip text={METRIC_DEFINITIONS['CAGR']}>{cagrText}</Tooltip>;
        } else {
          cagrColor = entry.cagr > 0 ? C.accent : (entry.cagr < 0 ? C.negative : C.textMuted);
          cagrCell  = <Tooltip text={METRIC_DEFINITIONS['CAGR']}>{cagrText}</Tooltip>;
        }

        return (
          <div key={label} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
            <span style={{ color:C.textMuted, fontSize:S.body }}><MetricLabel label={label} /></span>
            {yearLabels.map((_, i) => (
              <span key={i} style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>{fmt(display[i])}</span>
            ))}
            <span style={{ color:cagrColor, fontSize:S.body, fontWeight:W.semi, textAlign:'right' }}>
              {cagrCell}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// WHAT IS NOT AVAILABLE ABOUT THESE FIGURES, DERIVED FROM THE FIGURES THEMSELVES.
//
// THE NOTE THIS REPLACES WAS A FIXED SENTENCE and it was wrong on most companies that saw it.
// It printed, whenever the market data had not come from the quote endpoint, that "the sector,
// the dividend and the 52-week range are not shown" - while the table immediately above it was
// showing the 52-week high and low from the same payload. A note contradicted by the table it
// sits under teaches a reader to stop reading notes.
//
// Two rules, and both are the point. A field is named ONLY IF IT IS ABSENT FROM THIS PAYLOAD,
// so the note cannot call a shown figure missing. And it is worded as a fact about the FIGURE,
// never about a quote, a feed or an endpoint: where the number came from does not change what
// the reader should do with it, and the 52-week range is exactly as useful either way.
//
// Split into two sentences rather than joined with 'and', because all three fields absent plus
// the computed capitalisation runs past the twenty-five word sentence limit in COPY_STYLE.
// THE NAME TO PRINT. With the 'N/A' sentinel gone, `company.name` can be null for a symbol
// neither the quote nor the roster can name, and React renders null as an empty space - a
// heading with nothing in it. The ticker is always there, so it is the floor.
function nameOf(c) {
  return (c && (c.name || c.ticker)) || '';
}

// THE FINANCIALS BANNER, for every combination of the three presentations. The sentence it
// replaces said "Corporate and banking metrics are shown in separate sections" whatever the
// pair was - so two insurers were told about banking metrics that are not on their page, and a
// bank with an insurer was told nothing about the difference between them.
// F1. The clause form of each withheld-multiple reason, for the headline sentence above the
// comps table. The full sentences are in NA_REASONS and stay in the per-cell tooltip; the
// headline needs a clause so that naming the reason costs one short sentence rather than three.
const NM_CLAUSE = {
  nm_negative_base: 'its base is zero or negative',
  nm_negative_ev:   'net cash exceeds its market capitalisation',
  nm_extreme:       'its base is so close to zero that the ratio runs into hundreds',
  nr:               'the figure it needs is not reported',
  fx_unpinned:      'the exchange rates for the period could not be pinned down',
};
const nmClause = (reason) => NM_CLAUSE[reason] || 'the figures it needs are not available';

function bannerText(p1, p2) {
  const both = (k) => p1 === k && p2 === k;
  const has  = (k) => p1 === k || p2 === k;
  if (both('bank'))    return 'Both companies are banks or lenders.';
  if (both('insurer')) return 'Both companies are insurers.';
  if (has('bank') && has('insurer')) return 'This deal pairs a lender with an insurer.';
  if (has('insurer'))  return 'This is a cross-sector deal: one company is an insurer.';
  return 'This is a cross-sector deal: one company is in the financial sector.';
}

function figureNotes(c) {
  const absent = [];
  if (!c.sector) absent.push('the sector');
  // THE YIELD, because the yield is the field the table above shows. The dividend per share
  // can be known from the cash flow while the yield still is not, and it is the shown figure
  // a reader is owed an account of.
  if (c.dividend_yield == null) absent.push('the dividend yield');
  if (c.week52_high == null || c.week52_low == null) absent.push('the 52-week range');
  const out = [];
  if (absent.length === 1) out.push(`${absent[0]} is not available`);
  else if (absent.length > 1) out.push(`${absent.slice(0, -1).join(', ')} and ${absent[absent.length - 1]} are not available`);
  // A-T3-55: said only when it really was multiplied out, never when it was observed.
  if (c.market_cap_computed) out.push('the market capitalisation is calculated from the price and the share count');
  return out;
}

function FigureAvailabilityNote({ c1, c2 }) {
  const rows = [c1, c2].filter(Boolean).map(c => {
    if (c.market_data_as_of) {
      return [c.ticker, `the price and market capitalisation are as at ${c.market_data_as_of}, and every other figure is current`];
    }
    const notes = figureNotes(c);
    return notes.length ? [c.ticker, notes.join('. ')] : null;
  }).filter(Boolean);
  if (!rows.length) return null;
  return (
    <div style={{ marginTop:SP.md }}>
      {rows.map(([ticker, body]) => (
        <p key={ticker} style={{ color:C.target, fontSize:S.small, lineHeight:1.6, margin:0 }}>
          {`${ticker}: ${body}.`}
        </p>
      ))}
    </div>
  );
}

function OverviewTab({ c1, c2 }) {
  return (
    <>
    <MetricTable c1={c1} c2={c2} metrics={[
      ['Sector',         c1.sector || 'n.a.',    c2.sector || 'n.a.'],
      ['Market capitalisation',     fmt(c1.market_cap),    fmt(c2.market_cap)],
      ['Current price',  c1.current_price ? fmtRs(c1.current_price) : 'N/A', c2.current_price ? fmtRs(c2.current_price) : 'N/A'],
      ['P/E ratio',      fmtN(c1.pe_ratio),     fmtN(c2.pe_ratio)],
      ['P/B ratio',      fmtN(c1.pb_ratio),     fmtN(c2.pb_ratio)],
      ['Beta',           fmtN(c1.beta),          fmtN(c2.beta)],
      ['Dividend yield', fmtPct(c1.dividend_yield), fmtPct(c2.dividend_yield)],
      ['52-week high',       c1.week52_high ? fmtRs(c1.week52_high) : 'N/A', c2.week52_high ? fmtRs(c2.week52_high) : 'N/A'],
      ['52-week low',        c1.week52_low  ? fmtRs(c1.week52_low)  : 'N/A', c2.week52_low  ? fmtRs(c2.week52_low)  : 'N/A'],
    ]} />
    <FxBasisNote c1={c1} c2={c2} />
    <FigureAvailabilityNote c1={c1} c2={c2} />
    </>
  );
}

// WHERE GROSS PROFIT AND GROSS MARGIN USED TO BE: one line and a marker. The
// explanation itself is the 'gross-profit' topic in explanations.py.
function GrossProfitNote() {
  return (
    <p style={{ color:C.textFaint, fontSize:S.small, marginTop:`-${SP.xs}`, marginBottom:SP.lg, lineHeight:1.5 }}>
      Gross profit and gross margin aren't shown, because Indian companies don't report them.
      <InfoMarker id="gross-profit" />
    </p>
  );
}

function FinancialsTab({ c1, c2, historicalAlignment }) {
  const r1 = c1.ratios || {};
  const r2 = c2.ratios || {};
  const bm1 = c1.banking_metrics || {};
  const bm2 = c2.banking_metrics || {};
  const f1 = !!c1.is_financial;
  const f2 = !!c2.is_financial;
  const anyFinancial  = f1 || f2;
  const anyStandard   = !f1 || !f2;
  // A BANK IS NOT AN INSURER, and `is_financial` cannot tell them apart. The backend now names
  // the presentation each company takes - 'bank', 'insurer' or 'corporate' - so the heading, the
  // rows and the ratio block below can each ask the right question. Reading is_financial for
  // all three is what put a Banking heading over four rows of 'N/A (insurer)'.
  const p1 = c1.presentation || (f1 ? 'bank' : 'corporate');
  const p2 = c2.presentation || (f2 ? 'bank' : 'corporate');
  const b1 = p1 === 'bank', b2 = p2 === 'bank';
  const i1 = p1 === 'insurer', i2 = p2 === 'insurer';
  const anyBank    = b1 || b2;
  // P4b. Whether fee and other income is available for EITHER bank side. Where it is not, the
  // non-interest income and pre-provision rows are not shown at all: pre-provision operating
  // profit is net interest income PLUS fee income, and without the second term it is just the
  // first. Measured 7 October 2026, 0 of 26 banks and lenders in the frozen store carry it.
  const feeIncome  = (b1 && bm1.non_interest_income != null)
                  || (b2 && bm2.non_interest_income != null);
  const anyInsurer = i1 || i2;

  const sIS = (c, field) => c.is_financial ? 'N/A (bank)' : fmt(c[field]);
  // AN INSURER'S OWN ROWS. A company that is not an insurer has no cell in this table at all,
  // so the dash is a column marker rather than a missing figure.
  const insIS = (c, field) => ((c.presentation === 'insurer') ? fmt(c[field]) : '-');
  const bIS = (c, bm, field) => { if (!c.is_financial) return 'N/A (non-bank)'; if (bm && bm.not_applicable_insurer) return 'N/A (insurer)'; return fmt(bm[field]); };
  // Operating income is taken from the data source's EBIT row where it exists,
  // because that is the row satisfying EBITDA = operating income + D&A. Where it does
  // not exist we fall back to the source's "Operating Income" row, which is a
  // different measure - so that case is MARKED rather than passed off as the same
  // figure. Same principle as the PPOP dagger directly below.
  const oiCell = (c) => {
    if (c.is_financial) return 'N/A (bank)';
    if (c.operating_income === null || c.operating_income === undefined) return 'N/A';
    const v = fmt(c.operating_income);
    return c.operating_income_row === 'EBIT' ? v : `${v} \u2021`;
  };
  // A PPOP that is NII alone is marked with a dagger rather than hidden, so the
  // reader can see why it equals the row above it.
  const ppopCell = (c, bm) => {
    const v = bIS(c, bm, 'ppop');
    return (bm && bm.ppop_partial && v !== 'N/A' && !String(v).startsWith('N/A')) ? `${v} †` : v;
  };
  const pctRet = (c, bm, r, field) => fmtRatioPct(c.is_financial ? bm[field] : r[field]);
  const marginVal = (c, r, field) => c.is_financial ? 'N/A (bank)' : fmtRatioPct(r[field]);
  const roceVal = (c, r) => c.is_financial ? 'N/A (bank)' : fmtRatioPct(r.roce);
  const levVal = (c, r, field) => c.is_financial ? 'N/A (bank)' : fmtMultiple(r[field]);
  const nimVal = (c, bm) => { if (!c.is_financial) return 'N/A (non-bank)'; if (bm && bm.not_applicable_insurer) return 'N/A (insurer)'; const v = bm.nim; return (v === null || v === undefined) ? 'N/A: see note' : fmtRatioPct(v); };
  const ctiVal = (c, bm) => { if (!c.is_financial) return 'N/A (non-bank)'; if (bm && bm.not_applicable_insurer) return 'N/A (insurer)'; const v = bm.cost_to_income; return (v === null || v === undefined) ? 'N/A: see note' : fmtRatioPct(v); };

  const naStdIS = {};
  ['Revenue','Operating income','EBITDA'].forEach(m => {
    if (f1) naStdIS[`${m}_c1`] = 'bank_no_standard_is';
    if (f2) naStdIS[`${m}_c2`] = 'bank_no_standard_is';
  });
  // The backend suppresses operating income when the reported figures contradict each
  // other, and sends the machine key for the reason. Without this the cell would show
  // a bare N/A with nothing to click, which is the failure mode this project treats as
  // worse than the number itself.
  if (!f1 && c1.operating_income_suppressed) naStdIS['Operating income_c1'] = c1.operating_income_suppressed;
  if (!f2 && c2.operating_income_suppressed) naStdIS['Operating income_c2'] = c2.operating_income_suppressed;
  const naBankIS = {};
  ['Net interest income','Non-interest income','Pre-provision operating profit'].forEach(m => {
    if (!f1) naBankIS[`${m}_c1`] = 'nonbank_no_banking';
    if (!f2) naBankIS[`${m}_c2`] = 'nonbank_no_banking';
  });
  // A financial company whose row simply is not in the data still needs an
  // explanation - without one the cell renders a bare N/A with nothing to click.
  [[f1, bm1, 'c1'], [f2, bm2, 'c2']].forEach(([isFin, bm, slot]) => {
    if (!isFin || !bm || bm.not_applicable_insurer) return;
    if (bm.net_interest_income === null || bm.net_interest_income === undefined) {
      naBankIS[`Net interest income_${slot}`] = 'nii_not_reported';
    }
    if (bm.non_interest_income === null || bm.non_interest_income === undefined) {
      naBankIS[`Non-interest income_${slot}`] = 'non_ii_not_reported';
    }
  });
  const naMargins = {};
  ['EBITDA margin','Net profit margin'].forEach(m => {
    if (f1) naMargins[`${m}_c1`] = 'bank_no_margin';
    if (f2) naMargins[`${m}_c2`] = 'bank_no_margin';
  });
  const naReturns = {};
  if (f1) naReturns['Return on capital employed (ROCE)_c1'] = 'bank_no_roce';
  else if (r1.roce === null) naReturns['Return on capital employed (ROCE)_c1'] = 'ROCE_missing';
  if (f2) naReturns['Return on capital employed (ROCE)_c2'] = 'bank_no_roce';
  else if (r2.roce === null) naReturns['Return on capital employed (ROCE)_c2'] = 'ROCE_missing';
  // A company whose book equity is negative gets no ROE and no Debt / Equity from the
  // backend, for the reason in NA_REASONS.negative_equity. Without these two lines the cell
  // would read a bare N/A with nothing to click, which is worse than the wrong number it
  // replaced.
  const negEq = (c) => c && c.equity !== null && c.equity !== undefined && c.equity <= 0;
  if (negEq(c1)) naReturns['Return on equity (ROE)_c1'] = 'negative_equity';
  if (negEq(c2)) naReturns['Return on equity (ROE)_c2'] = 'negative_equity';
  const naLeverage = {};
  const levKeys = { 'Debt / Equity':'debt_equity_bank', 'Net debt / EBITDA':'net_debt_ebitda_bank', 'Interest coverage':'interest_coverage_bank' };
  Object.keys(levKeys).forEach(m => {
    if (f1) naLeverage[`${m}_c1`] = levKeys[m];
    if (f2) naLeverage[`${m}_c2`] = levKeys[m];
  });
  if (!f1 && negEq(c1)) naLeverage['Debt / Equity_c1'] = 'negative_equity';
  if (!f2 && negEq(c2)) naLeverage['Debt / Equity_c2'] = 'negative_equity';
  // Interest coverage and ROCE are suppressed by the backend on a negative EBITDA and on a
  // negative capital employed. Each cell says which, rather than a bare N/A.
  const negEbitda = (c) => c && c.ebitda !== null && c.ebitda !== undefined && c.ebitda <= 0;
  const negCapEmp = (c) => c && c.total_assets !== null && c.total_assets !== undefined
    && c.current_liabilities !== null && c.current_liabilities !== undefined
    && (c.total_assets - c.current_liabilities) <= 0;
  if (!f1 && negEbitda(c1)) naLeverage['Interest coverage_c1'] = 'negative_ebitda_coverage';
  if (!f2 && negEbitda(c2)) naLeverage['Interest coverage_c2'] = 'negative_ebitda_coverage';
  if (!f1 && negEbitda(c1)) naLeverage['Net debt / EBITDA_c1'] = 'negative_ebitda_leverage';
  if (!f2 && negEbitda(c2)) naLeverage['Net debt / EBITDA_c2'] = 'negative_ebitda_leverage';
  if (!f1 && negCapEmp(c1)) naReturns['Return on capital employed (ROCE)_c1'] = 'negative_capital_employed';
  if (!f2 && negCapEmp(c2)) naReturns['Return on capital employed (ROCE)_c2'] = 'negative_capital_employed';
  const naBanking = {};
  if (f1) { if (bm1.nim == null) naBanking['Net interest margin (NIM)_c1'] = 'nim_missing'; if (bm1.cost_to_income == null) naBanking['Cost-to-income ratio_c1'] = 'Cost-to-Income_financial'; }
  if (f2) { if (bm2.nim == null) naBanking['Net interest margin (NIM)_c2'] = 'nim_missing'; if (bm2.cost_to_income == null) naBanking['Cost-to-income ratio_c2'] = 'Cost-to-Income_financial'; }
  const naBankMetrics = {};
  if (!f1) { naBankMetrics['Net interest margin (NIM)_c1'] = 'nonbank_no_banking'; naBankMetrics['Cost-to-income ratio_c1'] = 'nonbank_no_banking'; }
  if (!f2) { naBankMetrics['Net interest margin (NIM)_c2'] = 'nonbank_no_banking'; naBankMetrics['Cost-to-income ratio_c2'] = 'nonbank_no_banking'; }
  const naCashFlow = {
    'Free cash flow_c1': (c1.free_cashflow !== null && c1.free_cashflow <= 0) ? 'Free cash flow_negative' : undefined,
    'Free cash flow_c2': (c2.free_cashflow !== null && c2.free_cashflow <= 0) ? 'Free cash flow_negative' : undefined,
  };

  return (
    <div>
      {anyFinancial && (
        <div style={{ backgroundColor:'#0a1a2a', borderRadius:R.sm, padding:'12px 16px', border:'1px solid #1a3a5a', marginBottom:SP.md }}>
          <p style={{ color:'#4a8aaa', fontSize:S.small, lineHeight:'1.5', margin:0 }}>
            ℹ️ {bannerText(p1, p2)} Financials are presented using each company's reported accounting measures.
          </p>
        </div>
      )}

      {anyStandard && (
        <>
          <SectionLabel text="Income Statement: Standard (Annual)" />
          <IncomeStatementTable c1={c1} c2={c2} naReasons={naStdIS} metrics={[
            ['Revenue',          sIS(c1,'revenue'),          sIS(c2,'revenue'),          'revenue'],
            ['Operating income', oiCell(c1),                 oiCell(c2),                 null],
            ['EBITDA',           sIS(c1,'ebitda'),           sIS(c2,'ebitda'),           'ebitda'],
            ['Net income',       fmt(c1.net_income),         fmt(c2.net_income),         'net_income'],
          ]} />
          <GrossProfitNote />
        </>
      )}

      {anyBank && (
        <>
          <SectionLabel text="Income Statement: Banking (Annual)" />
          <IncomeStatementTable c1={c1} c2={c2} naReasons={naBankIS} metrics={[
            ['Net interest income',            bIS(c1,bm1,'net_interest_income'), bIS(c2,bm2,'net_interest_income'), 'net_interest_income'],
            ...(feeIncome ? [
            ['Non-interest income',            bIS(c1,bm1,'non_interest_income'), bIS(c2,bm2,'non_interest_income'), null],
            ['Pre-provision operating profit', ppopCell(c1,bm1), ppopCell(c2,bm2), 'ppop'],
            ] : []),
            ['Net income',                     fmt(c1.net_income),               fmt(c2.net_income),               'net_income'],
          ]} />
        {/* P4b. The dagger footnote belonged to a row that is no longer shown; in its place,
            one sentence saying why the row is absent. */}
        {!feeIncome && (
          <p style={{ color:C.textFaint, fontSize:S.small, marginTop:'-2px', marginBottom:SP.lg }}>
            Fee and other income is not available in Dealquire's data, so pre-provision operating profit is not shown.
          </p>
        )}
        {feeIncome && (bm1.ppop_partial || bm2.ppop_partial) && (
          <p style={{ color:C.textFaint, fontSize:S.small, marginTop:'-2px', marginBottom:SP.lg }}>
            † Pre-provision operating profit is net interest income alone here, because this company doesn't report non-interest income separately.
          </p>
        )}
        </>
      )}

      {/* THE INSURER STATEMENT. Five rows, and every one of them is present for all ten listed
          insurers in the frozen store - which is the test a row has to pass to be here at all.
          The three rows the bank table asks for (non-interest income, pre-provision operating
          profit, provisions) exist for NONE of them, and net interest income is excluded on its
          own evidence: the annual frame carries it because the source computes it from a
          template, while the quarterly frame carries it for three of ten, which is how one
          insurer came to show an LTM net interest income of -Rs 61 cr. No dagger footnote
          either - it belongs to pre-provision operating profit, which is not here. */}
      {anyInsurer && (
        <>
          <SectionLabel text="Income Statement: Insurance (Annual)" />
          <IncomeStatementTable c1={c1} c2={c2} metrics={[
            ['Total revenue',              insIS(c1,'revenue'),               insIS(c2,'revenue'),               'revenue'],
            ['Net claims and benefits',     insIS(c1,'policyholder_benefits'), insIS(c2,'policyholder_benefits'), null],
            ['Total expenses',             insIS(c1,'total_expenses'),        insIS(c2,'total_expenses'),        null],
            ['Pre-tax income',             insIS(c1,'pretax_income'),         insIS(c2,'pretax_income'),         null],
            ['Net income',                 insIS(c1,'net_income'),            insIS(c2,'net_income'),            'net_income'],
          ]} />
          <p style={{ color:C.textFaint, fontSize:S.small, marginTop:'-2px', marginBottom:SP.lg, lineHeight:1.5 }}>
            Insurers do not report net interest income or pre-provision operating profit, so those bank measures are not shown.
          </p>
        </>
      )}

      {anyStandard && ((!f1 && c1.operating_income_row === 'Operating Income') ||
                       (!f2 && c2.operating_income_row === 'Operating Income')) && (
        <p style={{ color:C.textFaint, fontSize:S.small, marginTop:'-2px', marginBottom:SP.lg }}>
          ‡ Operating income comes from the data source's own "Operating Income" row, because there is no EBIT row for this company. <strong>That row doesn't reconcile with EBITDA.</strong> Operating income plus depreciation equals EBITDA on this row for 8.6% of the roster, against 99.2% for the EBIT row. Treat it as approximate and check the filing.
        </p>
      )}

      {(anyStandard || anyFinancial) && (
        <p style={{ color:C.textFaint, fontSize:S.small, marginTop:'-4px', marginBottom:SP.xlr }}>
          LTM = last twelve months. Valuation and the Merger Model use annual figures.
        </p>
      )}

      <SectionLabel text="Balance Sheet" />
      <MetricTable c1={c1} c2={c2} naReasons={cashSplitNaReasons(c1, c2)} metrics={[
        ['Total assets',              fmt(c1.total_assets), fmt(c2.total_assets)],
        ['Total debt',                fmt(c1.total_debt),   fmt(c2.total_debt)],
        ['Cash & cash equivalents',   ceCell(c1),           ceCell(c2)],
        ['Short-term investments',    stiCell(c1),          stiCell(c2)],
        ['Cash including short-term investments', fmt(c1.cash),         fmt(c2.cash)],
        ...((c1.restricted_cash != null || c2.restricted_cash != null)
          ? [['Restricted cash, reported but not deducted', rcCell(c1), rcCell(c2)]] : []),
        ['Net debt',                  fmt(c1.net_debt),     fmt(c2.net_debt)],
        ['Shareholders\' equity',       fmt(c1.equity),       fmt(c2.equity)],
      ]} />
      <NetDebtNote c1={c1} c2={c2} />

      <SectionLabel text="Cash Flow" />
      <MetricTable c1={c1} c2={c2} naReasons={naCashFlow} metrics={[
        ['Operating cash flow', fmt(c1.operating_cashflow), fmt(c2.operating_cashflow)],
        ['Capital expenditure', fmt(c1.capex),              fmt(c2.capex)],
        ['Free cash flow',      fmt(c1.free_cashflow),      fmt(c2.free_cashflow)],
      ]} />

      <SectionLabel text="Key Ratios" />

      {anyStandard && (
        <>
          <SubLabel text="Profitability" />
          <MetricTable c1={c1} c2={c2} naReasons={naMargins} metrics={[
            ['EBITDA margin',     marginVal(c1,r1,'ebitda_margin'), marginVal(c2,r2,'ebitda_margin')],
            ['Net profit margin', marginVal(c1,r1,'net_margin'),    marginVal(c2,r2,'net_margin')],
          ]} />
        </>
      )}

      <SubLabel text="Returns" />
      <MetricTable c1={c1} c2={c2} naReasons={naReturns} metrics={[
        ['Return on equity (ROE)',            pctRet(c1,bm1,r1,'roe'), pctRet(c2,bm2,r2,'roe')],
        ['Return on assets (ROA)',            pctRet(c1,bm1,r1,'roa'), pctRet(c2,bm2,r2,'roa')],
        ['Return on capital employed (ROCE)', roceVal(c1,r1),          roceVal(c2,r2)],
      ]} />

      {anyStandard && (
        <>
          <SubLabel text="Leverage" />
          <MetricTable c1={c1} c2={c2} naReasons={naLeverage} metrics={[
            ['Debt / Equity',     levVal(c1,r1,'debt_to_equity'),     levVal(c2,r2,'debt_to_equity')],
            ['Net debt / EBITDA', levVal(c1,r1,'net_debt_to_ebitda'), levVal(c2,r2,'net_debt_to_ebitda')],
            ['Interest coverage', levVal(c1,r1,'interest_coverage'),  levVal(c2,r2,'interest_coverage')],
          ]} />
        </>
      )}

      {/* B1. BANKS ONLY. With the two spread ratios gone for an insurer this section rendered
          as a heading, an empty table and a note - so an insurer pair was shown a Banking
          Metrics block with nothing in it. */}
      {anyBank && (
        <>
          <SubLabel text="Banking Metrics" />
          <MetricTable c1={c1} c2={c2} naReasons={{ ...naBanking, ...naBankMetrics }} metrics={[
            ['Net interest margin (NIM)', nimVal(c1,bm1), nimVal(c2,bm2)],
            ['Cost-to-income ratio',      ctiVal(c1,bm1), ctiVal(c2,bm2)],
          ]} />
          <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'12px 16px', border:`1px solid ${C.lineSoft}`, marginTop:SP.xs }}>
            <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:'1.5', margin:0 }}>
              Banks and lending NBFCs are measured differently, so revenue, EBITDA and capital-employed ratios read as not applicable.
            </p>
          </div>
        </>
      )}

      <SectionLabel text="Historical Trend & Growth" />
      {historicalAlignment && historicalAlignment.note_needed && (() => {
        const { aligned_to, c1_latest_available, c1_ticker, c2_ticker } = historicalAlignment;
        const c1IsLagging = c1_latest_available === aligned_to;
        const laggingTicker  = c1IsLagging ? c1_ticker : c2_ticker;
        const moreRecentYear = c1IsLagging ? historicalAlignment.c2_latest_available : c1_latest_available;
        return (
          <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'12px 16px', border:`1px solid ${C.lineSoft}`, marginBottom:SP.md }}>
            <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:'1.5', margin:0 }}>
              ℹ️ Both companies are compared as of {aligned_to}, the latest year both have reported. {laggingTicker} hasn't reported {moreRecentYear} yet, so its latest figures shown are for {aligned_to}.
            </p>
          </div>
        );
      })()}
      {(() => {
        // B1. AN INSURER TAKES ITS OWN ROW SET. The bank set was used for every financial
        // company, so an insurer pair was shown three years of net interest income and a
        // pre-provision operating profit - neither of which an insurer reports.
        // P4b. The pre-provision row leaves the historical table too, on the same test.
        const dropPpop = (rows) => (feeIncome ? rows
          : rows.filter(r => r !== 'Pre-provision operating profit'));
        const histRows0 = !f1 && !f2 ? NONFIN_HIST_ROWS
          : (i1 && i2 ? INSURER_HIST_ROWS
          : (anyInsurer && !anyBank ? MIXED_INSURER_HIST_ROWS
          : (f1 && f2 ? FIN_HIST_ROWS : MIXED_HIST_ROWS)));
        const histRows = dropPpop(histRows0);
        return (
          <>
            <SubLabel text={c1.ticker} />
            <HistoricalSubTable company={c1} rowLabels={histRows} />
            <SubLabel text={c2.ticker} />
            <HistoricalSubTable company={c2} rowLabels={histRows} />
          </>
        );
      })()}
      <FxBasisNote c1={c1} c2={c2} />
      <OneOffNote c1={c1} c2={c2} />
    </div>
  );
}

function ValuationTab({ c1, c2 }) {
  const ModelBadge = ({ model }) => {
    const colours = { 'DCF':{bg:'#1a2a1a',text:C.accent,border:C.accent}, 'DDM':{bg:'#1a2a3a',text:C.info,border:C.info}, 'EV/EBITDA':{bg:'#2a2a1a',text:C.target,border:C.target} };
    const c = colours[model] || colours['DCF'];
    return <span style={{ backgroundColor:c.bg, color:c.text, border:`1px solid ${c.border}`, fontSize:S.micro, fontWeight:W.bold, padding:'3px 8px', borderRadius:R.pill, marginLeft:SP.med }}>{model}</span>;
  };

  const ValuationCard = ({ company }) => {
    const val = company.valuation;
    if (!val) return (
      <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
        <h3 style={{ color:C.accent, marginBottom:SP.md }}>{company.ticker}</h3>
        <p style={{ color:C.textMuted, fontSize:S.body, marginBottom:SP.sm }}>{nameOf(company)}</p>
        <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:SP.lgr, border:`1px solid ${C.lineSoft}`, marginTop:SP.lg }}>
          <p style={{ color:C.textFaint, fontSize:S.body, lineHeight:'1.6' }}>No valuation: free cash flow is negative and there isn't enough EBITDA data for {company.ticker}.</p>
        </div>
      </div>
    );
    if (company.is_distressed) return (
      <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
        <h3 style={{ color:'#E0B45C', marginBottom:SP.md }}>{company.ticker}</h3>
        <p style={{ color:C.textMuted, fontSize:S.body, marginBottom:SP.sm }}>{nameOf(company)}</p>
        <div style={{ backgroundColor:'#2d1f0f', borderRadius:R.sm, padding:SP.lgr, border:'2px solid #E0B45C', marginTop:SP.lg }}>
          <p style={{ color:'#E0B45C', fontSize:S.body, fontWeight:W.bold, lineHeight:'1.6' }}>
            ⚠️ Distressed company: negative book equity ({fmt(company.equity)}). The valuation is shown for reference only; equity value means nothing here.
          </p>
        </div>
      </div>
    );
    const upside      = val.upside_downside_pct;
    // Colour carries size as well as direction: teal above +20%, muted from 0% to +20%, red
    // below 0%, grey at exactly 0%. The text beside it states direction only.
    const upsideColor = upside > 20 ? C.accent : upside > 0 ? C.textMuted : upside < 0 ? C.negative : C.textFaint;
    const signal      = upside == null ? '' : upside > 0 ? '▲ Intrinsic value is higher' : upside < 0 ? '▼ Intrinsic value is lower' : '● Intrinsic value is the same';
    const Header = ({ model }) => (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:SP.xlr }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', marginBottom:SP.xs }}>
            <h3 style={{ color:C.accent }}>{company.ticker}</h3><ModelBadge model={model} />
          </div>
          <p style={{ color:C.textMuted, fontSize:S.body }}>{nameOf(company)}</p>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ color:upsideColor, fontSize:S.lede, fontWeight:W.bold }}>{upside > 0 ? '+' : ''}{upside?.toFixed(1)}%</div>
          <div style={{ color:upsideColor, fontSize:S.small, fontWeight:W.bold }}>{signal}</div>
          <div style={{ color:C.textMuted, fontSize:S.micro, marginTop:SP.xxs }}>Against the current market price</div>
        </div>
      </div>
    );
    // B1. ONE SENTENCE WHERE THE VALUE IS BELOW HALF OR ABOVE DOUBLE THE PRICE. The figure
    // stays - it is what the model says - but a reader who does not already know the model's
    // weakness is owed the warning beside the number rather than in a methodology topic.
    const FarNote = ({ v }) => (v && v.far_from_price_note ? (
      <div style={{ marginTop:SP.md, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'10px 14px' }}>
        <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:'1.5', margin:0 }}>{v.far_from_price_note}</p>
      </div>
    ) : null);
    const Row = ({ label, value }) => (
      <div style={{ display:'flex', justifyContent:'space-between', borderBottom:`1px solid ${C.lineSoft}`, padding:'10px 0' }}>
        <span style={{ color:C.textMuted, fontSize:S.body }}><MetricLabel label={label} /></span>
        <span style={{ color:C.text, fontSize:S.body, fontWeight:W.medium }}>{value}</span>
      </div>
    );
    const AssumptionRow = ({ label, value }) => (
      <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0' }}>
        <span style={{ color:C.textFaint, fontSize:S.small }}><MetricLabel label={label} /></span>
        <span style={{ color:C.textMuted, fontSize:S.small }}>{value}</span>
      </div>
    );
    // Net debt's build-up beside the figure it explains - the same components as the
    // Financials tab, so a difference from another source can be traced here too.
    const NetDebtBuild = () => {
      const line = (label, value) => (
        <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 0 2px 14px' }}>
          <span style={{ color:C.textFaint, fontSize:S.small }}>{label}</span>
          <span style={{ color:C.textFaint, fontSize:S.small }}>{value}</span>
        </div>
      );
      const s = company.cash_split_status;
      return (
        <div style={{ borderBottom:`1px solid ${C.lineSoft}`, padding:'4px 0 8px' }}>
          {line('Total debt', fmt(company.total_debt))}
          {s === 'split' ? (
            <>
              {line('(-) Cash & cash equivalents', fmt(company.cash_and_equivalents))}
              {line('(-) Short-term investments', fmt(company.short_term_investments))}
            </>
          ) : line(s === 'no_short_term_investments_row' ? '(-) Cash & cash equivalents' : '(-) Cash (components not reported)', fmt(company.cash))}
          {company.restricted_cash !== null && company.restricted_cash !== undefined && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 0 2px 28px' }}>
              <span style={{ color:C.textFaint, fontSize:S.small, fontStyle:'italic' }}>
                <Tooltip text={METRIC_DEFINITIONS['Restricted cash, reported but not deducted']}>Restricted cash, reported but not deducted</Tooltip>
              </span>
              <span style={{ color:C.textFaint, fontSize:S.small, fontStyle:'italic' }}>{fmt(company.restricted_cash)}</span>
            </div>
          )}
        </div>
      );
    };
    // C3. ANY METHOD THAT COULD NOT RUN, with the reason the payload carries. This used to be
    // a DDM-only branch, so a P/B or EV/EBITDA valuation that could not run fell through to the
    // DCF layout and read assumptions that were not there.
    if (val.unavailable) return (
      <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
        <div style={{ display:'flex', alignItems:'center', marginBottom:SP.sml }}><h3 style={{ color:C.accent }}>{company.ticker}</h3><ModelBadge model={val.model} /></div>
        <p style={{ color:C.textMuted, fontSize:S.body, marginBottom:SP.lg }}>{nameOf(company)}</p>
        <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:SP.lgr, border:`1px solid ${C.lineSoft}` }}>
          <p style={{ color:C.textMuted, fontSize:S.body, lineHeight:'1.6' }}>{val.reason}</p>
        </div>
        {/* 5a. ALWAYS THE PRICE. It read "Current price: N/A" beside a no-value message,
            because the unavailable returns carried no price - and the price is the one figure
            that was never in doubt. The company dict is the fallback. */}
        <p style={{ color:C.textFaint, fontSize:S.small, marginTop:SP.md }}>Current price: {fmtRs(val.current_price != null ? val.current_price : company.current_price)}</p>
      </div>
    );
    // C1. PRICE TO BOOK, the headline for every financial company. The peer count and the
    // median are shown beside the value, because a multiple taken from four peers and one
    // taken from nine are not the same evidence.
    if (val.model === 'P/B') {
      return (
        <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:SP.sml }}><h3 style={{ color:C.accent }}>{company.ticker}</h3><ModelBadge model="P/B" /></div>
          <p style={{ color:C.textMuted, fontSize:S.body, marginBottom:SP.lg }}>{nameOf(company)}</p>
          <Row label="Intrinsic value / share"  value={fmtRs(val.intrinsic_per_share)} />
          <Row label="Current price"            value={fmtRs(val.current_price)} />
          <Row label="Against the price"        value={fmtSignedPct(val.upside_downside_pct)} />
          <FarNote v={val} />
          <Row label="Book value per share"  value={fmtRs(val.book_value_per_share)} />
          <Row label="Peer median P/B"       value={`${val.peer_median_pb}x`} />
          <Row label="Peers in the median"   value={`${val.peer_count}`} />
          {val.method_note && (
            <p style={{ color:C.textFaint, fontSize:S.small, marginTop:SP.md, lineHeight:1.5 }}>{val.method_note}</p>
          )}
          {val.method_fallback_note && (
            <p style={{ color:C.textFaint, fontSize:S.small, marginTop:SP.sml, lineHeight:1.5 }}>{val.method_fallback_note}</p>
          )}
          {/* B1. THE SECOND VALUATION, BENEATH THE HEADLINE AND NEVER INSTEAD OF IT. The
              backend fills `valuation_cross_check` for banks and lending NBFCs only, and only
              where the annual dividend is known - insurers never get one. The 5 October
              measurement that withdrew it stands: that is why it sits here rather than at the
              top, and why its note says why a dividend-based value is low. */}
          {company.valuation_cross_check && (
            <div style={{ marginTop:SP.lg, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:SP.lg }}>
              <p style={{ color:C.textFaint, fontSize:S.micro, marginBottom:SP.med }}>Second valuation: dividend discount model</p>
              <Row label="Value per share"   value={fmtRs(company.valuation_cross_check.intrinsic_per_share)} />
              <Row label="Against the price" value={fmtSignedPct(company.valuation_cross_check.upside_downside_pct)} />
              {/* C1. ONE NOTE. FarNote used to sit here and said the same thing about low
                  payouts as the note below it. `method_note` IS the caution sentence when
                  the value is far from the price, so there is nothing left for FarNote to
                  add - and the reader is told once. */}
              {company.valuation_cross_check.method_note && (
                <p style={{ color:C.textFaint, fontSize:S.small, marginTop:SP.med, lineHeight:1.5 }}>{company.valuation_cross_check.method_note}</p>
              )}
            </div>
          )}
        </div>
      );
    }
    if (val.model === 'DDM') {
      const a = val.assumptions;
      return (
        <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:SP.xlr }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', marginBottom:SP.xs }}>
                <h3 style={{ color:C.accent }}>{company.ticker}</h3><ModelBadge model="DDM" />
              </div>
              <p style={{ color:C.textMuted, fontSize:S.body }}>{nameOf(company)}</p>
            </div>
          </div>
          <FarNote v={val} />
          <Row label="Intrinsic value / share" value={fmtRs(val.intrinsic_per_share)} />
          <Row label="Current price"           value={fmtRs(val.current_price)} />
          <div style={{ marginTop:SP.lgr, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'13px 14px', border:'1px solid #2a2a2a' }}>
            <p style={{ color:'#777', fontSize:S.small, lineHeight:'1.6', margin:0 }}>
              {val.method_note || 'DDM values the dividend stream only, so it undervalues companies that retain earnings. See the P/B and P/E comparables for a market view.'}
            </p>
          </div>
          <div style={{ marginTop:SP.md, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:SP.lg }}>
            <p style={{ color:C.textFaint, fontSize:S.micro, marginBottom:SP.med }}>DDM Assumptions (Default)</p>
            <AssumptionRow label="Cost of equity"      value={`${(a.cost_of_equity*100).toFixed(1)}%`} />
            <AssumptionRow label="Dividend growth rate" value={`${(a.dividend_growth*100).toFixed(1)}%`} />
            <AssumptionRow label="Dividend per share (current year)"   value={fmtRs(a.dps_current)} />
            <AssumptionRow label="Dividend per share (next year, estimated)" value={fmtRs(a.dps_next_year)} />
          </div>
        </div>
      );
    }
    if (val.model === 'EV/EBITDA') {
      const a = val.assumptions;
      return (
        <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
          <Header model="EV/EBITDA" />
          <FarNote v={val} />
          <Row label="Intrinsic value / share"  value={fmtRs(val.intrinsic_per_share)} />
          <Row label="Current price"            value={fmtRs(val.current_price)} />
          <Row label="Implied enterprise value" value={fmt(val.implied_ev)} />
          <Row label="(-) Net debt"             value={fmt(val.net_debt)} />
          <NetDebtBuild />
          <Row label="Implied equity value"     value={fmt(val.equity_value)} />
          <div style={{ marginTop:SP.xlr, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:SP.lg }}>
            <p style={{ color:C.textFaint, fontSize:S.micro, marginBottom:SP.med }}>EV/EBITDA Assumptions (Default)</p>
            <AssumptionRow label="EV/EBITDA multiple" value={`${a.ev_ebitda_multiple}x`} />
            <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0' }}><span style={{ color:C.textFaint, fontSize:S.small }}>Sector</span><span style={{ color:C.textMuted, fontSize:S.small }}>{a.sector || 'n.a.'}</span></div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0' }}><span style={{ color:C.textFaint, fontSize:S.small }}>EBITDA</span><span style={{ color:C.textMuted, fontSize:S.small }}>{fmt(a.ebitda)}</span></div>
          </div>
          {/* THE DEFAULT, NAMED. An unknown sector takes 12x where Technology takes 22x, and
              nothing used to say so - two valuations 1.8 times apart looked identical. */}
          {val.sector_default_note && (
            <div style={{ marginTop:SP.md, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'10px 14px' }}>
              <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:'1.5' }}>{val.sector_default_note}</p>
            </div>
          )}
          <div style={{ marginTop:SP.md, backgroundColor:C.tintTarget, borderRadius:R.sm, padding:'10px 14px', border:'1px solid #3a3a1a' }}>
            <p style={{ color:'#8a8a4a', fontSize:S.small, lineHeight:'1.5' }}>ℹ️ No DCF because free cash flow is negative. The EV/EBITDA multiple is used instead.</p>
          </div>
        </div>
      );
    }
    return (
      <div style={{ flex:1, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}` }}>
        <Header model="DCF" />
        <FarNote v={val} />
        <Row label="Intrinsic value / share" value={fmtRs(val.intrinsic_per_share)} />
        <Row label="Current price"           value={fmtRs(val.current_price)} />
        <Row label="Enterprise value"        value={fmt(val.enterprise_value)} />
        <Row label="(-) Net debt"            value={fmt(val.net_debt)} />
        <NetDebtBuild />
        <Row label="Equity value"            value={fmt(val.equity_value)} />
        <Row label="Present value of free cash flows"              value={fmt(val.sum_pv_fcfs)} />
        <Row label="Present value of terminal value"    value={fmt(val.pv_terminal_value)} />
        <div style={{ marginTop:SP.xlr, backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:SP.lg }}>
          <p style={{ color:C.textFaint, fontSize:S.micro, marginBottom:SP.med }}>DCF Assumptions (Default)</p>
          <AssumptionRow label="WACC"            value={`${(val.assumptions.wacc*100).toFixed(1)}%`} />
          <AssumptionRow label="Free cash flow growth"      value={`${(val.assumptions.fcf_growth*100).toFixed(1)}%`} />
          <AssumptionRow label="Terminal growth" value={`${(val.assumptions.terminal_growth*100).toFixed(1)}%`} />
          <AssumptionRow label="Forecast period" value={`${val.assumptions.forecast_years} years`} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display:'flex', gap:SP.xlr, marginBottom:SP.lg }}>
        <ValuationCard company={c1} />
        <ValuationCard company={c2} />
      </div>
      <div style={{ backgroundColor:C.surfaceAlt, borderRadius:R.sm, padding:'14px 18px', border:`1px solid ${C.lineSoft}` }}>
        <p style={{ color:C.textFaint, fontSize:S.small }}>The valuation model for each company is chosen automatically.<InfoMarker id="valuation-method-selection" /></p>
      </div>
      <FxBasisNote c1={c1} c2={c2} />
      <OneOffNote c1={c1} c2={c2} />
    </div>
  );
}

// The five figures the Deal-o-Meter used to weigh, reported as themselves. The dial, the
// 0-100 score and the five rating words - Avoid, Cautious, Neutral, Favourable,
// Mechanically favourable - were removed on 18-Sep-2026: nothing measured the weights they
// were built from, and one number hid which input had moved it. See
// calculate_deal_characteristics in app.py for the full reasoning.
//
// NOTHING HERE IS COLOURED BY VALUE, and that is the point of the panel. Five figures in
// four different units are not on a scale, so a green one and a red one would reinstate
// the ranking the score used to assert. The only colour is on the basis word, which says
// where a figure came from rather than whether it is good.
const DEAL_CHARACTERISTIC_TIPS = {
  eps_accretion: METRIC_DEFINITIONS['EPS change'],
  offer_premium: 'How far above the target\'s current market price the offer is. You set it; the default is 20%.',
  synergy_coverage: 'The present value of the modelled synergies as a percentage of the premium paid above market. At 100% the synergies are worth exactly what was paid for control.',
  net_debt_to_ebitda_post: METRIC_DEFINITIONS['Net debt / EBITDA'],
  size_ratio: 'The target\'s market capitalisation divided by the acquirer\'s. Above 1.0 the target is worth more than the company buying it.',
};
const BASIS_WORD = { measured: 'Measured', input: 'Your input', assumption: 'Assumption' };
const BASIS_COLOUR = { measured: C.accent, input: C.info, assumption: C.offer };

function DealCharacteristics({ dealCharacteristics }) {
  const dc = dealCharacteristics;
  if (!dc) return null;
  // At 50% or below there is no combined entity, so four of the five describe something
  // that does not exist. The backend suppresses them and names the reason.
  if (dc.suppressed) {
    return (
      <div style={{ backgroundColor:C.surface, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.1em', marginBottom:SP.sm }}>Deal Characteristics</p>
        <p style={{ color:C.textMuted, fontSize:S.body, margin:`0 0 ${SP.xs} 0` }}>
          <Tooltip naLabel="Why are these not shown?" naReason={dc.suppressed_reason}>
            <span style={{ color:C.textFaint }}>Not applicable at this ownership level</span>
          </Tooltip>
        </p>
        <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.5, margin:0 }}>{dc.suppressed_reason}</p>
      </div>
    );
  }
  const fmtVal = (m) => {
    // A COLON, like the six other places in this file that write this. The em dash here was
    // the only one, and a dash joining two parts of a label is what COPY_STYLE.md forbids.
    if (m.value === null || m.value === undefined) return 'N/A: see note';
    // The premium is the one input among the five: as entered, not at two decimals.
    if (m.key === 'offer_premium') return fmtPctInput(m.value);
    return m.unit === '%' ? `${Number(m.value).toFixed(2)}%` : `${Number(m.value).toFixed(2)}x`;
  };
  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.md, padding:SP.huge, border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.1em', marginBottom:SP.med }}>Deal Characteristics</p>
      <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.5, margin:`0 0 ${SP.xlr} 0` }}>{dc.basis_note}</p>
      {dc.implausible && (
        <div style={{ backgroundColor:'#3a1f1f', border:`1px solid ${C.negative}`, borderRadius:R.sm, padding:'14px 18px', marginBottom:SP.xlr }}>
          <span style={{ color:C.negative, fontSize:S.h3, fontWeight:W.bold }}>{dc.implausible_reason}</span>
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:SP.med }}>
        {(dc.metrics || []).map((m) => (
          <div key={m.key} style={{ borderTop:`1px solid ${C.line}`, paddingTop:SP.med }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:SP.med }}>
              <span style={{ color:C.textMuted, fontSize:S.body }}>
                <Tooltip text={DEAL_CHARACTERISTIC_TIPS[m.key]}>{m.label}</Tooltip>
              </span>
              <span style={{ color:C.text, fontSize:S.h3, fontWeight:W.bold, whiteSpace:'nowrap' }}>{fmtVal(m)}</span>
            </div>
            <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.5, margin:`${SP.xs} 0 0 0` }}>
              <span style={{ color:BASIS_COLOUR[m.basis] || C.textMuted, fontWeight:W.semi }}>{BASIS_WORD[m.basis] || m.basis}.</span>{' '}
              {m.unavailable ? m.unavailable : m.basis_note}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// THE CASH OPTION sits here because this is where the mix is chosen. `note` is the text
// app.py wrote, the same words the tearsheet and the workbook carry; the component does not
// compose it. Absent before the first analysis, so it renders only when it arrives.
function DealTypeToggle({ dealType, onChange, disabled, note }) {
  const options = [{key:'all_cash',label:'All cash'},{key:'50_50',label:'50 / 50'},{key:'all_stock',label:'All stock'}];
  return (
    <div style={{ marginBottom:SP.xlr }}>
      <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.08em', marginBottom:SP.med }}>Deal Structure</p>
      <div style={{ display:'flex', gap:SP.sm }}>
        {options.map(opt => {
          const active = dealType === opt.key;
          return <button key={opt.key} onClick={() => !disabled && onChange(opt.key)} disabled={disabled} style={{ flex:1, padding:'10px 0', fontSize:S.body, fontWeight:W.semi, border:active?`1px solid ${C.accent}`:`1px solid ${C.line}`, borderRadius:R.sm, cursor:disabled?'not-allowed':'pointer', backgroundColor:active?C.tintAccent:C.surface, color:active?C.accent:C.textFaint, transition:'all 0.2s', opacity:disabled?0.5:1 }}>{opt.label}</button>;
        })}
      </div>
      <p style={{ color:C.textFaint, fontSize:S.micro, marginTop:SP.sm }}>
        {dealType==='all_cash'  && 'All debt-funded: no new shares, but the most interest.'}
        {dealType==='50_50'     && 'Half cash from new debt, half new shares, so both effects are moderate.'}
        {dealType==='all_stock' && 'All paid in new shares: no new debt or interest, but the most dilution.'}
      </p>
      {note && (
        <p style={{ color:C.textFaint, fontSize:S.micro, lineHeight:1.6, marginTop:SP.med }}>
          {note}<InfoMarker id="open-offer-cash-option" />
        </p>
      )}
    </div>
  );
}

// Signed out, the export buttons are replaced by one line saying what signing in gives.
// Exports are free; the account exists so the files can be tied to a history.
function SignInToExport({ onNeedAuth }) {
  return (
    <div style={{ backgroundColor:C.surfaceAlt, border:`1px solid ${C.line}`, borderRadius:R.md,
                  padding:`${SP.lg} ${SP.xlr}`, marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.6, margin:0 }}>
        A ten-page PDF tearsheet and a full Excel model, built from the figures on this page. Both are free, and sign-in keeps them in your history.{' '}
        <span onClick={onNeedAuth} style={{ color:C.accent, cursor:'pointer', fontWeight:W.semi }}>Sign in or create an account</span>
      </p>
    </div>
  );
}

// THE NAME THE BROWSER SAVES. The export routes build a name carrying the premium, the
// ownership and the time - Dealquire_HCLTECH_INFY_100pct_20prem_20261006-1457.xlsx - and the
// frontend was throwing it away: `a.download` was set to Dealquire_<t1>_<t2>.<ext>, which
// overrides Content-Disposition completely. Two runs of one pair therefore landed as the same
// name, and the browser appended "(2)".
//
// Content-Disposition is readable only because the API now exposes it; if it ever stops being
// readable the fallback builds the same shape client-side rather than silently losing the
// inputs again.
function downloadName(res, ticker1, ticker2, ownership, premium, ext) {
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (m && m[1]) return decodeURIComponent(m[1].trim());
  const now = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`
    + `-${p(now.getHours())}${p(now.getMinutes())}`;
  return `Dealquire_${ticker1}_${ticker2}_${ownership}pct_${premium}prem_${stamp}.${ext}`;
}

function ExportButton({ ticker1, ticker2, dealType, premium, ownership = 100, user, onNeedAuth, showDialog }) {
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // An export runs the same analysis an Analyse does, so it gets the same notice after the same
  // fifteen seconds. One flag covers both buttons: only one runs at a time.
  const [slow, setSlow] = useState(false);
  const slowTimer = useRef(null);

  const startSlowTimer = () => {
    clearTimeout(slowTimer.current);
    slowTimer.current = setTimeout(() => setSlow(true), SLOW_NOTICE_MS);
  };

  const stopSlowTimer = () => {
    clearTimeout(slowTimer.current);
    setSlow(false);
  };

  // Every param is URL-encoded: three NSE tickers contain '&' (ARE&M, M&M,
  // M&MFIN) and would otherwise truncate at the ampersand server-side.
  const enc = encodeURIComponent;

  const handleExport = async () => {
    setLoading(true); startSlowTimer();
    try {
      const res = await fetch(`${API_BASE}/api/export?ticker1=${enc(ticker1)}&ticker2=${enc(ticker2)}&deal_type=${enc(dealType)}&premium=${enc(premium)}&ownership=${enc(ownership)}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err.error === 'auth_required') { onNeedAuth(); }
        // A RATE LIMIT IS NOT A FAILURE, and a dialog headed "Export failed" sends the reader
        // looking for a fault in the deal. The server's message names the limit it hit.
        // A REFUSAL FOR CAPACITY IS NOT A FAILURE. 503 with error 'server_busy' means the file
        // was never started - the deal is fine and so is the account - so this is not headed
        // "Export failed", and it carries the one capacity sentence rather than the server's.
        else if (err.error === 'server_busy') { showDialog({ title:'Please try again shortly', body: AT_CAPACITY, confirmLabel:'Close' }); }
        else if (res.status === 429) { showDialog({ title:'Too many exports for now', body: err.message || 'You have made as many requests as this hour allows. Please try again a little later.', confirmLabel:'Close' }); }
        else { showDialog({ title:'Export failed', body: err.message || 'Something went wrong generating that file.', confirmLabel:'Close' }); }
        stopSlowTimer();
        setLoading(false); setPdfLoading(false);
        return;
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = downloadName(res, ticker1, ticker2, ownership, premium, 'xlsx');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showDialog({ title:'Export failed', body:"We couldn't reach the server. Please try again in a moment.", confirmLabel:'Close' });
    }
    stopSlowTimer();
    setLoading(false);
  };

  const handlePdfExport = async () => {
    setPdfLoading(true); startSlowTimer();
    try {
      const res = await fetch(`${API_BASE}/api/export/pdf?ticker1=${enc(ticker1)}&ticker2=${enc(ticker2)}&deal_type=${enc(dealType)}&premium=${enc(premium)}&ownership=${enc(ownership)}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err.error === 'auth_required') { onNeedAuth(); }
        // A REFUSAL FOR CAPACITY IS NOT A FAILURE. 503 with error 'server_busy' means the file
        // was never started - the deal is fine and so is the account - so this is not headed
        // "Export failed", and it carries the one capacity sentence rather than the server's.
        else if (err.error === 'server_busy') { showDialog({ title:'Please try again shortly', body: AT_CAPACITY, confirmLabel:'Close' }); }
        else if (res.status === 429) { showDialog({ title:'Too many exports for now', body: err.message || 'You have made as many requests as this hour allows. Please try again a little later.', confirmLabel:'Close' }); }
        else { showDialog({ title:'Export failed', body: err.message || 'Something went wrong generating that file.', confirmLabel:'Close' }); }
        stopSlowTimer();
        setLoading(false); setPdfLoading(false);
        return;
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = downloadName(res, ticker1, ticker2, ownership, premium, 'pdf');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showDialog({ title:'PDF export failed', body:"We couldn't reach the server. Please try again in a moment.", confirmLabel:'Close' });
    }
    stopSlowTimer();
    setPdfLoading(false);
  };

  const btnBase = { display:'inline-flex', alignItems:'center', gap:SP.sm, padding:'10px 18px', fontSize:S.body, fontWeight:W.semi, borderRadius:R.sm };
  const busy = loading || pdfLoading;

  return (
    <div style={{ marginBottom:SP.xlr }}>
      {user ? (
        <div style={{ display:'flex', gap:SP.med, flexWrap:'wrap' }}>
          <button onClick={handleExport} disabled={busy} style={{ ...btnBase, border:`1px solid ${C.accent}`, cursor:busy?'not-allowed':'pointer', backgroundColor:C.tintAccent, color:C.accent, opacity:busy&&!loading?0.5:1 }}>
            <span>{loading ? 'Generating...' : '⬇ Export to Excel'}</span>
          </button>
          <button onClick={handlePdfExport} disabled={busy} style={{ ...btnBase, border:`1px solid ${C.accent}`, cursor:busy?'not-allowed':'pointer', backgroundColor:C.tintAccent, color:C.accent, opacity:busy&&!pdfLoading?0.5:1 }}>
            <span>{pdfLoading ? 'Building tearsheet...' : '⬇ Export PDF tearsheet'}</span>
          </button>
        </div>
      ) : (
        <SignInToExport onNeedAuth={onNeedAuth} />
      )}
      {pdfLoading && (
        <p style={{ color:C.textMuted, fontSize:S.small, marginTop:SP.sm }}>
          Building the tearsheet. It runs the full analysis plus the optimizer, sensitivity, credit and ownership checks, so it takes longer than the Excel export.
        </p>
      )}
      {slow && busy && (
        <p style={{ color:C.textMuted, fontSize:S.small, marginTop:SP.sm }}>
          {SLOW_NOTICE}
        </p>
      )}
    </div>
  );
}

function RegulatoryFlagsCard({ flags }) {
  if (!flags || flags.length === 0) return null;
  const sevColors = {
    required: C.negative, likely: C.target, exempt: C.accent,
    info: C.textMuted, not_assessed: C.textMuted,
  };
  const sevBgs = {
    required: C.tintNeg, likely: '#2a2a0a', exempt: C.tintAccent,
    info: C.surface, not_assessed: C.surface,
  };
  const sevBorders = {
    required: C.negative, likely: C.target, exempt: C.accent,
    info: C.line, not_assessed: C.line,
  };
  return (
    <div style={{ marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.1em', marginBottom:SP.md }}>Regulatory Flags</p>
      <div style={{ display:'flex', flexDirection:'column', gap:SP.sm }}>
        {flags.map((f, i) => {
          const col = sevColors[f.severity] || C.textMuted;
          const bg  = sevBgs[f.severity] || C.surface;
          const bdr = sevBorders[f.severity] || C.line;
          return (
            <div key={i} style={{ backgroundColor:bg, borderRadius:R.md, padding:'14px 18px', border:`1px solid ${bdr}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:SP.med, marginBottom:SP.sml }}>
                <span style={{ width:'8px', height:'8px', borderRadius:'50%', backgroundColor:col, flexShrink:0 }} />
                <Tooltip text={f.detail}>
                  <span style={{ color:col, fontSize:S.body, fontWeight:W.semi, cursor:'help' }}>{f.headline}</span>
                </Tooltip>
              </div>
              <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.5, margin:0, paddingLeft:'18px' }}>{f.detail}</p>
            </div>
          );
        })}
      </div>
      <p style={{ color:C.textFaint, fontSize:S.micro, fontStyle:'italic', marginTop:SP.med, lineHeight:1.5 }}>
        Based on standard thresholds, not legal advice. Whether they apply also depends on India-specific financials, group structure, promoter holdings and the consideration.
      </p>
    </div>
  );
}

// The smallest peer count that gets a published median. Mirrors MIN_PEERS_FOR_STAT in
// backend/app.py: below it the statistics are withheld rather than computed on two names.
const MIN_PEERS_FOR_STAT = 3;

function recomputeComps(peers, targetCurrent, targetImplied, isFinancial, prevStats) {
  const keys = isFinancial ? ['pb', 'pe'] : ['ev_ebitda', 'ev_revenue', 'pe'];
  const stats = {};
  const premium_discount = {};
  for (const key of keys) {
    // Mirrors compute_comp_stats in backend/app.py. A peer flagged 'outlier' carries a
    // real value so the table can show it, but it must NOT move the median. This
    // filter was previously missing here, so editing the peer list silently pulled
    // outliers back into a median the backend had excluded them from.
    const clean = peers
      .filter(p => p[key] !== null && p[key] !== undefined && p[key + '_reason'] !== 'outlier')
      .map(p => p[key]);
    const outlierTickers = peers
      .filter(p => p[key + '_reason'] === 'outlier')
      .map(p => p.ticker)
      .filter(Boolean);
    // A sector-level match has its statistics withheld by the backend. Editing the
    // list must not resurrect a median that was deliberately not published.
    const suppressed = prevStats && prevStats[key] && prevStats[key].suppressed;
    if (suppressed) {
      stats[key] = { median: null, mean: null, min: null, max: null, count: clean.length,
                     outliers_excluded: outlierTickers.length, outlier_tickers: outlierTickers,
                     suppressed };
    } else if (clean.length >= MIN_PEERS_FOR_STAT) {
      const sorted = [...clean].sort((a, b) => a - b);
      const n = sorted.length;
      const med = n % 2 === 0
        ? Math.round(((sorted[n / 2 - 1] + sorted[n / 2]) / 2) * 100) / 100
        : Math.round(sorted[Math.floor(n / 2)] * 100) / 100;
      stats[key] = {
        median: med,
        mean:   Math.round((clean.reduce((s, v) => s + v, 0) / clean.length) * 100) / 100,
        min:    Math.round(Math.min(...clean) * 100) / 100,
        max:    Math.round(Math.max(...clean) * 100) / 100,
        count:  clean.length,
        outliers_excluded: outlierTickers.length,
        outlier_tickers:   outlierTickers,
      };
    } else {
      stats[key] = { median: null, mean: null, min: null, max: null, count: clean.length,
                     insufficient: true, outliers_excluded: outlierTickers.length,
                     outlier_tickers: outlierTickers };
    }
    const med = stats[key].median;
    const entry = { current_vs_median: null, deal_vs_median: null };
    if (med !== null && med !== undefined) {
      if (targetCurrent[key] != null)
        entry.current_vs_median = Math.round((targetCurrent[key] / med - 1) * 1000) / 10;
      if (targetImplied[key] != null)
        entry.deal_vs_median = Math.round((targetImplied[key] / med - 1) * 1000) / 10;
    }
    premium_discount[key] = entry;
  }
  return { stats, premium_discount };
}

// ---- SEBI SAST: ONE OF THE OFFER-PRICE BENCHMARKS ------------------------------------
// Displayed information only - it feeds nothing. THE WORDING IS LOAD-BEARING: the figure
// is a LOWER BOUND on the statutory minimum offer price, never the minimum itself,
// because every other benchmark turns on the acquirer's private dealings or on a
// valuation and cannot be computed from public data. Do not shorten any label or note
// below into something that reads as "the minimum offer price".
// THE COUNT IS NOT STATED ANY MORE, and that is deliberate: Regulation 8(2) has six clauses,
// (a) to (f), and (f) - the per share value under sub-regulation (5) - applies only where that
// sub-regulation does, so "five" was wrong as a statement about the regulation (read 26-Sep-2026
// in the text consolidated to 05-Dec-2025). The benchmarks and why only one is computable are
// the 'sast-pricing' topic in explanations.py; the marker on the panel heading opens it.
const SAST_VWAP_TIP = 'One of the benchmarks that set the minimum open offer price under SEBI (SAST) Regulation 8(2). The minimum is the highest of them, so this is a lower bound.';
const SAST_CLEARS_TIP = 'Clearing this one benchmark doesn\'t mean the offer meets the minimum. The benchmarks that can\'t be computed from public data can set it higher.';

// WHAT WAS MEASURED, SAID PLAINLY. SAST takes the exchange with the highest volume, but
// yfinance has no BSE history for some targets (INFY and RELIANCE among them, verified
// 16-Sep-2026). These sentences name a winning exchange only when both were measured;
// otherwise they say which exchange was measured and what that leaves untested.
const sastShares = (v) => (v === null || v === undefined) ? 'N/A' : `${fmtIndianAbs(Number(v), 0)} shares`;
// EACH LEG NAMES ITS OWN UNMEASURED EXCHANGE. An exchange can carry volume over sixty trading
// days and none over the twelve calendar months before this month, so one list cannot answer for
// both sentences.
const sastUnmeasured = (s, leg) => {
  const miss = ((leg === 'vwap' ? s.vwap_exchanges_not_measured
                                : s.turnover_exchanges_not_measured)
                || s.exchanges_not_measured || []).join(' and ');
  return miss ? `${miss} volume` : 'Volume on the other exchange';
};
const sastTurnoverSentence = (s, ticker) => {
  if (s.turnover_pct === null || s.turnover_pct === undefined) return null;
  const share = `${Number(s.turnover_pct).toFixed(1)}%`;
  const when = `in the twelve calendar months ${fmtDate(s.turnover_window_start)} to ${fmtDate(s.turnover_window_end)}`;
  const by = s.traded_shares_by_exchange || {};
  if (s.turnover_comparison_run) {
    const parts = Object.keys(by).filter(ex => by[ex] !== null && by[ex] !== undefined)
      .map(ex => `${ex} ${sastShares(by[ex])}`).join(', ');
    return `${share} of ${ticker}'s total shares traded on ${s.turnover_exchange} ${when}. ${s.turnover_exchange} was the higher of the two exchanges (${parts}). The SAST threshold for frequently traded shares is 10%.`;
  }
  return `${share} of ${ticker}'s total shares traded on ${s.turnover_exchange} ${when}. ${sastUnmeasured(s, 'turnover')} couldn't be measured from the data source, so this is ${s.turnover_exchange} alone and no exchange comparison was made. SAST applies the 10% threshold on any exchange, so if the other exchange traded more, the true figure is higher than shown.`;
};
const sastVwapBasisSentence = (s) => {
  const days = `the ${s.trading_days_found} trading days ${fmtDate(s.vwap_window_start)} to ${fmtDate(s.vwap_window_end)}`;
  const by = s.vwap_volume_by_exchange || {};
  if (s.vwap_comparison_run) {
    const parts = Object.keys(by).map(ex => `${ex} ${sastShares(by[ex])}`).join(', ');
    return `Each day's typical price, (high + low + close) ÷ 3, weighted by that day's volume. The exchange is ${s.vwap_exchange}, which had the higher volume over ${days}. Volumes were ${parts}.`;
  }
  // TWO SENTENCES EACH SIDE OF 25 WORDS. The window dates and the exchange name put both of these
  // over the limit once they are in the sentence, and a reader meets them as one run of text.
  return `Each day's typical price, (high + low + close) ÷ 3, weighted by that day's volume on ${s.vwap_exchange}. The window is ${days}. ${sastUnmeasured(s, 'vwap')} couldn't be measured from the data source. So the statutory test of taking the exchange with the highest volume hasn't been applied.`;
};

function SastFloorPanel({ sast, ticker }) {
  if (!sast) return null;
  const s = sast;
  // BELOW THE TRIGGER THERE IS NO OFFER TO PRICE. SEBI (SAST) requires an open offer at 25%
  // of voting rights or more; under that, a panel headed with an offer price answers a
  // question that does not arise. The threshold ladder still renders, at every level: how
  // the obligations arrive as a stake grows is the instructive part.
  if (s.open_offer_triggered === false) {
    return (
      <div style={{ backgroundColor:C.surface, borderRadius:R.md, padding:'18px 22px', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.08em', margin:'0 0 4px' }}>
          SAST Open Offer: Not Triggered at {s.ownership_pct}%
          <InfoMarker id="sast-pricing" />
        </p>
        <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:'1.55', margin:'10px 0 0' }}>
          Acquiring {s.ownership_pct}% of {ticker} does not trigger a mandatory open offer:
          SEBI (SAST) requires one at {s.open_offer_trigger_pct}% of the voting rights or
          more. Shares the acquirer already holds count towards the threshold, and the data
          shows none.
        </p>
      </div>
    );
  }
  const line = (label, value, tip, color) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${C.lineSoft}` }}>
      <span style={{ color:C.textMuted, fontSize:S.body }}>{tip ? <Tooltip text={tip}>{label}</Tooltip> : label}</span>
      <span style={{ color:color || C.text, fontSize:S.body, fontWeight:W.semi, textAlign:'right' }}>{value}</span>
    </div>
  );
  const prose = { color:C.textFaint, fontSize:S.small, lineHeight:'1.55', margin:'10px 0 0' };
  const turnoverSentence = sastTurnoverSentence(s, ticker);

  let body;
  if (s.status === 'computed') {
    const clears = s.clears_vwap_leg;
    const gap = s.offer_vs_vwap_pct;
    body = (
      <>
        {line('60-day VWAP (1 of 5 SAST benchmarks)', fmtRs(s.vwap), SAST_VWAP_TIP)}
        {line(`Implied offer price at ${fmtPctInput(s.offer_premium_pct)} premium`, fmtRs(s.offer_price),
              `The share price of ${fmtRs(s.current_price)} plus the offer premium for this deal.`)}
        {line('Clears the VWAP benchmark?',
              clears === null || clears === undefined ? 'N/A'
                : (clears ? `Yes, ${Math.abs(gap).toFixed(1)}% above` : `No, ${Math.abs(gap).toFixed(1)}% below`),
              SAST_CLEARS_TIP,
              clears === false ? C.negative : undefined)}
        <p style={prose}>
          {clears === false
            ? 'At this premium the offer price is below the 60-day VWAP. The minimum is the highest of the five benchmarks, so an offer at this price falls short whatever the other four are.'
            : 'At this premium the offer price is at or above the 60-day VWAP, which clears one benchmark. The four below can set the minimum higher.'}
        </p>
      </>
    );
  } else if (s.status === 'not_frequently_traded') {
    body = (
      <p style={{ ...prose, color:C.textMuted, fontSize:S.body }}>
        {ticker} {s.turnover_comparison_run ? "isn't frequently traded" : "isn't frequently traded on the exchange measured"}: {turnoverSentence} So the 60-day VWAP benchmark does not apply. The minimum would instead rest on valuation measures and the acquirer's own dealing benchmarks, none of which are computed here.
      </p>
    );
  } else if (s.status === 'insufficient_history') {
    body = (
      <p style={{ ...prose, color:C.textMuted, fontSize:S.body }}>
        Only {s.trading_days_found} trading days of price and volume history were found, fewer than the 60 the benchmark needs, so no VWAP is shown.
      </p>
    );
  } else {
    body = (
      <p style={{ ...prose, color:C.textMuted, fontSize:S.body }}>
        The SAST price benchmark couldn't be checked{s.reason ? `: ${s.reason}` : ''}. No figure is shown.
      </p>
    );
  }

  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.md, padding:'18px 22px', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.08em', margin:'0 0 4px' }}>
        SAST Open Offer Price: Lower Bound Only
        <InfoMarker id="sast-pricing" />
      </p>
      {body}
      {/* THE COUNT COMES FROM THE PAYLOAD. This line used to read "The other four
          benchmarks can't be computed from public data" whatever had happened, which is
          right when the VWAP was computed and wrong when it was not: after
          not_frequently_traded or insufficient_history none of the five is computed, not
          four of them. */}
      <p style={{ ...prose, color:C.textMuted }}>
        {(s.benchmarks_computed || 0) > 0
          ? "The other four benchmarks can't be computed from public data, and any of them can set the minimum higher."
          : `None of the ${s.benchmarks_total || 5} benchmarks is computed for this company. Any of them can set the minimum higher.`}
      </p>
      {s.status === 'computed' && (
        <p style={prose}>
          How the VWAP is computed: {sastVwapBasisSentence(s)} The exchange's own VWAP is built
          from every trade and will differ slightly. Frequently traded: {turnoverSentence}
        </p>
      )}
    </div>
  );
}

// ---- OWNERSHIP LEVEL ---------------------------------------------------------------------
// Every component below reads proforma.deal_structure.accounting_treatment and the ownership
// block the backend sends; none re-derives the treatment from the percentage.

// THE DEAD ZONE, at the top of the tab. Above a private-sector target's 75% ceiling and up to
// the 90% delisting threshold there is no level a listed company can stay at.
function DeadZoneBanner({ reg }) {
  if (!reg || !reg.dead_zone) return null;
  return (
    <div style={{ backgroundColor:C.tintNeg, border:`2px solid ${C.negative}`, borderRadius:R.md, padding:'18px 22px', marginBottom:SP.xlr }}>
      <p style={{ color:C.negative, fontSize:S.small, fontWeight:W.bold, letterSpacing:'0.1em', margin:`0 0 ${SP.sm} 0` }}>
        Ownership Dead Zone at {reg.ownership_pct}%
      </p>
      <p style={{ color:C.text, fontSize:S.h3, fontWeight:W.semi, lineHeight:1.45, margin:0 }}>{reg.dead_zone_statement}</p>
    </div>
  );
}

// POOLING AGAINST THE ACQUISITION METHOD belongs on the card that states which treatment is
// applied. `poolingNote` names the goodwill this deal shows and the nil goodwill the same deal
// would show under pooling, and says that common control cannot be determined from this data.
// It is null at every level that consolidates nothing, because there is no goodwill to contrast.
function OwnershipTreatmentCard({ proforma, poolingNote }) {
  const o = proforma && proforma.ownership;
  if (!o) return null;
  const nciNote = proforma.synergies && proforma.synergies.nci_synergy_treatment;
  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.md, padding:'18px 22px', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.08em', margin:'0 0 4px' }}>Accounting Treatment at {o.pct}% owned</p>
      <p style={{ color:C.text, fontSize:S.h3, fontWeight:W.semi, margin:`0 0 ${SP.sm} 0` }}>{o.treatment_label}</p>
      <p style={{ color:C.textMuted, fontSize:S.body, lineHeight:1.55, margin:0 }}>{o.treatment_statement}</p>
      {nciNote && (
        <>
          <p style={{ color:C.text, fontSize:S.body, fontWeight:W.semi, margin:`${SP.md} 0 ${SP.xs} 0` }}>Synergies and the Non-Controlling Interest</p>
          <p style={{ color:C.textMuted, fontSize:S.body, lineHeight:1.55, margin:0 }}>{nciNote}</p>
        </>
      )}
      {/* A4: only a bank acquirer has one, because only a bank still reports under Indian GAAP. */}
      {o.indian_gaap_note && (
        <>
          <p style={{ color:C.text, fontSize:S.body, fontWeight:W.semi, margin:`${SP.md} 0 ${SP.xs} 0` }}>Indian GAAP, not Ind AS</p>
          <p style={{ color:C.textMuted, fontSize:S.body, lineHeight:1.55, margin:0 }}>{o.indian_gaap_note}</p>
        </>
      )}
      {poolingNote && (
        <>
          <p style={{ color:C.text, fontSize:S.body, fontWeight:W.semi, margin:`${SP.md} 0 ${SP.xs} 0` }}>
            The Acquisition Method, and Pooling of Interests<InfoMarker id="common-control-pooling" />
          </p>
          <p style={{ color:C.textMuted, fontSize:S.body, lineHeight:1.55, margin:0 }}>{poolingNote}</p>
        </>
      )}
      <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.5, margin:`${SP.md} 0 0 0` }}>{o.basis_note}</p>
    </div>
  );
}

// A suppressed block of the pro forma, with its reason written out rather than only on hover.
// `reason` comes from the payload: proforma.suppression_reasons[code], written once in
// app.py. See the note where the two entries used to sit in NA_REASONS.
function OwnershipSuppressedBlock({ code, reason, items }) {
  if (!code) return null;
  return (
    <div style={{ backgroundColor:C.surface, borderRadius:R.sm, padding:'14px 18px', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, margin:`0 0 ${SP.xs} 0` }}>
        <Tooltip naLabel="Why not shown?" naReason={reason}><span>{items}: not shown</span></Tooltip>
      </p>
      <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.5, margin:0 }}>{reason}</p>
    </div>
  );
}

// 20% to 50%: the acquirer's own P&L with ONE line for its share of the target's profit, and
// the investment at cost. Nothing here is combined, because nothing is.
function EquityMethodSection({ proforma }) {
  const ds = proforma.deal_structure, is_ = proforma.income_statement, bs = proforma.balance_sheet;
  // A target can lose money, and the share booked is then a share of a LOSS. Same word as
  // profit_or_loss_word in app.py, applied to the same figure.
  const plWord = (is_.share_of_profit !== null && is_.share_of_profit !== undefined
                  && is_.share_of_profit < 0) ? 'loss' : 'profit';
  const rows = [
    [`${ds.acquirer} net income (standalone)`, fmt(is_.ni_c1)],
    [`${ds.target} net income`, fmt(is_.ni_c2)],
    [`Share of ${ds.target} ${plWord} (${ds.ownership_pct}%), one line in ${ds.acquirer}'s profit and loss statement`, fmt(is_.share_of_profit)],
    ['Less: new acquisition interest', ds.new_annual_interest > 0 ? <>({fmt(ds.new_annual_interest)})</> : '—'],
    [`${ds.acquirer} net income including that share`, fmt(is_.ni_attributable)],
    [`Investment in ${ds.target}, at cost (balance sheet)`, fmt(bs.investment_at_cost)],
  ];
  return (
    <>
      <SectionLabel text="Equity Method Earnings" />
      <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
            <span style={{ color:C.textMuted, fontSize:S.body }}>{label}</span>
            <span style={{ color:C.text, fontSize:S.body, fontWeight:W.medium }}>{value}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// The regulatory consequence of the level chosen: thresholds and what they set, never advice
// on whether a transaction or a route is permissible.
function OwnershipLadderPanel({ reg }) {
  if (!reg) return null;
  const ps = reg.public_sector || {};
  const mark = (a) => a === true ? { t:'Applies here', c:C.target }
    : a === false ? { t:'Does not apply', c:C.textFaint }
    : { t:'Reference', c:C.textFaint };
  return (
    <div style={{ marginBottom:SP.lg }}>
      <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.1em', marginBottom:SP.md }}>Regulatory Thresholds at {reg.ownership_pct}%</p>
      {reg.dead_zone && (
        <div style={{ backgroundColor:C.tintNeg, border:`2px solid ${C.negative}`, borderRadius:R.md, padding:'14px 18px', marginBottom:SP.sm }}>
          <p style={{ color:C.negative, fontSize:S.body, fontWeight:W.bold, lineHeight:1.5, margin:0 }}>{reg.dead_zone_statement}</p>
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:SP.sm }}>
        {(reg.annotations || []).map(n => {
          const m = mark(n.applies);
          return (
            <div key={n.key} style={{ backgroundColor:C.surface, borderRadius:R.md, padding:'12px 18px', border:`1px solid ${n.applies === true ? C.target : C.line}` }}>
              <p style={{ color:n.applies === true ? C.text : C.textMuted, fontSize:S.body, fontWeight:W.semi, margin:'0 0 4px 0' }}>{n.headline}</p>
              <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.5, margin:0 }}>
                {n.detail}{n.topic && <InfoMarker id={n.topic} />}
              </p>
              <p style={{ color:m.c, fontSize:S.micro, margin:'4px 0 0 0', letterSpacing:'0.06em' }}>{m.t}</p>
            </div>
          );
        })}
      </div>
      <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.5, marginTop:SP.med }}>
        Public sector: {ps.basis}{ps.assumption ? ` ${ps.assumption}` : ''}
      </p>
      <p style={{ color:C.textFaint, fontSize:S.micro, fontStyle:'italic', marginTop:SP.sm, lineHeight:1.5 }}>{reg.disclaimer}</p>
    </div>
  );
}

function DealAnalysisTab({ proforma, c1, c2, dealCharacteristics, dealType, onDealTypeChange, ticker1, ticker2, comps: initialComps, regulatoryFlags, sastVwapLeg, premium, premiumInput, onPremiumInputChange, onPremiumChange, ownership = 100, ownershipInput = '100', onOwnershipInputChange, onOwnershipChange, ownershipRegulatory, regulatoryNotes, isAnalysing, tickers = [], user, onNeedAuth, showDialog }) {
  const [compsData, setCompsData]     = useState(initialComps);
  const [peerInput, setPeerInput]     = useState('');
  const [compsAdding, setCompsAdding]   = useState(false);
  const [compsError, setCompsError]     = useState('');
  const abortRef = useRef(null);

  const handlePremiumBlur = () => {
    const parsed = parseFloat(premiumInput);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      onPremiumInputChange(String(premium));
      return;
    }
    const clamped = Math.min(100, Math.max(0, parsed));
    onPremiumInputChange(String(clamped));
    if (clamped !== premium) onPremiumChange(clamped);
  };

  // Ownership acquired, validated the way the premium is: outside 1-100 reverts to the value
  // in force rather than re-running on a bad figure.
  const handleOwnershipBlur = () => {
    const parsed = parseFloat(ownershipInput);
    if (isNaN(parsed) || parsed < 1 || parsed > 100) {
      onOwnershipInputChange(String(ownership));
      return;
    }
    onOwnershipInputChange(String(parsed));
    if (parsed !== ownership) onOwnershipChange(parsed);
  };

  const removePeer = (ticker) => {
    if (!compsData || compsAdding) return;
    const newPeers       = compsData.peers.filter(p => p.ticker !== ticker);
    const newPeerTickers = compsData.peer_tickers.filter(t => t !== ticker);
    const { stats, premium_discount } = recomputeComps(
      newPeers, compsData.target_current, compsData.target_implied, compsData.is_financial, compsData.stats
    );
    setCompsData({ ...compsData, peers: newPeers, peer_tickers: newPeerTickers, stats, premium_discount });
  };

  const addPeer = async () => {
    const t = peerInput.trim().toUpperCase();
    if (!t) return;
    const current = compsData?.peer_tickers || [];
    if (current.includes(t)) { setPeerInput(''); return; }
    setPeerInput('');
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCompsAdding(true);
    setCompsError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/comps?target=${encodeURIComponent(compsData?.target_ticker || ticker2)}&peers=${encodeURIComponent(t)}&deal_type=${encodeURIComponent(dealType)}&premium=${premium}`,
        { signal: controller.signal }
      );
      const data = await res.json();
      if (data.error || !data.peers || data.peers.length === 0 || (data.invalid_peers && data.invalid_peers.includes(t))) {
        setCompsError(`'${t}' not found, so it wasn't added`);
      } else {
        const peerObj        = data.peers[0];
        const newPeers       = [...(compsData?.peers || []), peerObj];
        const newPeerTickers = [...current, t];
        const { stats, premium_discount } = recomputeComps(
          newPeers, compsData.target_current, compsData.target_implied, compsData.is_financial, compsData.stats
        );
        setCompsData({ ...compsData, peers: newPeers, peer_tickers: newPeerTickers, stats, premium_discount });
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
    setCompsAdding(false);
  };

  if (!proforma) return <div style={{ color:C.textFaint, textAlign:'center', padding:SP.xxl }}>No deal data available</div>;
  const { deal_structure:ds, income_statement:is_, balance_sheet:bs, accretion_dilution:ad, synergies:syn } = proforma;
  // The treatment as the backend recorded it. A payload without one predates ownership and
  // is a full acquisition.
  const treatment    = ds.accounting_treatment || 'consolidation_full';
  const consolidated = treatment === 'consolidation_full' || treatment === 'consolidation_with_nci';
  const withNci      = treatment === 'consolidation_with_nci';
  const sup          = proforma.suppressed || {};
  // One source for these texts, and it is app.py. The payload carries them.
  const supReason    = (code) => (proforma.suppression_reasons || {})[code];
  // 3c: an EBITDA synergy is only a line where both companies report an EBITDA. app.py decides
  // it and says why; the table and the sub-line below both read this.
  const ebitdaSynergyOk = (syn || {}).ebitda_synergy_meaningful !== false;
  const ownPct       = ds.ownership_pct ?? 100;
  const isImplausible = dealCharacteristics?.implausible || false;
  const adMeaningful = ad.is_meaningful !== false && !isImplausible;
  const adColor = !adMeaningful ? C.textMuted : (ad.is_accretive ? C.accent : C.negative);
  const adLabel = !adMeaningful ? 'Not meaningful' : (ad.is_accretive ? '▲ Accretive' : '▼ Dilutive');
  const DealRow = ({ label, value }) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
      <span style={{ color:C.textMuted, fontSize:S.body }}><MetricLabel label={label} /></span>
      <span style={{ color:C.text, fontSize:S.body, fontWeight:W.medium }}>{value}</span>
    </div>
  );

  return (
    <div>
      <DealTypeToggle dealType={dealType} onChange={onDealTypeChange} disabled={isAnalysing}
                      note={regulatoryNotes && regulatoryNotes.cash_option} />
      {/* The offer premium and ownership are open to everyone, signed in or not. They were
          gated behind a credit until 18-Sep-2026. */}
      <div style={{ marginBottom:SP.xlr, display:'flex', gap:SP.xxl, flexWrap:'wrap' }}>
        <div>
          <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.08em', marginBottom:SP.med }}>Offer premium (%)</p>
          <input
            type="number" min="0" max="100" step="1"
            value={premiumInput}
            onChange={e => onPremiumInputChange(e.target.value)}
            onBlur={handlePremiumBlur}
            onKeyDown={e => e.key === 'Enter' && handlePremiumBlur()}
            disabled={isAnalysing}
            style={{ width:'100px', padding:'10px 14px', fontSize:S.h3, fontWeight:W.semi, borderRadius:R.sm, border:`1px solid ${C.accent}`, backgroundColor: isAnalysing ? C.surface : C.tintAccent, color:C.accent, outline:'none', cursor: isAnalysing ? 'not-allowed' : 'text' }}
          />
          <span style={{ color:C.textFaint, fontSize:S.micro, marginLeft:SP.med }}>%</span>
        </div>
        <div>
          <p style={{ color:C.textMuted, fontSize:S.small, letterSpacing:'0.08em', marginBottom:SP.med }}>Ownership acquired (%)</p>
          <input
            type="number" min="1" max="100" step="1"
            value={ownershipInput}
            onChange={e => onOwnershipInputChange(e.target.value)}
            onBlur={handleOwnershipBlur}
            onKeyDown={e => e.key === 'Enter' && handleOwnershipBlur()}
            disabled={isAnalysing}
            style={{ width:'100px', padding:'10px 14px', fontSize:S.h3, fontWeight:W.semi, borderRadius:R.sm, border:`1px solid ${C.accent}`, backgroundColor: isAnalysing ? C.surface : C.tintAccent, color:C.accent, outline:'none', cursor: isAnalysing ? 'not-allowed' : 'text' }}
          />
          <span style={{ color:C.textFaint, fontSize:S.micro, marginLeft:SP.med }}>%</span>
        </div>
      </div>
      <DeadZoneBanner reg={ownershipRegulatory} />
      <ExportButton ticker1={ticker1} ticker2={ticker2} dealType={dealType} premium={premium} ownership={ownership} user={user} onNeedAuth={onNeedAuth} showDialog={showDialog} />
      <OwnershipTreatmentCard proforma={proforma} poolingNote={regulatoryNotes && regulatoryNotes.pooling} />
      <DealCharacteristics dealCharacteristics={dealCharacteristics} />
      <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, padding:SP.xl, border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <p style={{ color:C.textMuted, fontSize:S.small, marginBottom:SP.sml }}>{treatment === 'investment' ? 'Proposed investment' : 'Proposed acquisition'}</p>
            <h2 style={{ color:C.text, fontSize:S.h1 }}>
              <span style={{ color:C.accent }}>{ds.acquirer}</span>
              <span style={{ color:C.textFaint, margin:'0 12px' }}>{treatment === 'investment' ? 'invests in' : 'acquires'}{ownPct < 100 ? ` ${ownPct}% of` : ''}</span>
              <span style={{ color:C.target }}>{ds.target}</span>
            </h2>
          </div>
          <div style={{ textAlign:'right' }}>
            <p style={{ color:C.textMuted, fontSize:S.small }}>{ownPct < 100 ? `Consideration for ${ownPct}%` : 'Deal value'}</p>
            <p style={{ color:C.text, fontSize:S.lede, fontWeight:W.bold }}>{fmt(ds.deal_value)}</p>
            <p style={{ color:C.textFaint, fontSize:S.small }}>{fmtPctInput(ds.offer_premium_pct)} premium</p>
          </div>
        </div>
      </div>
      {treatment !== 'investment' && (
      <div style={{ backgroundColor:!adMeaningful?C.lineSoft:(ad.is_accretive?C.tintAccent:C.tintNeg), borderRadius:R.md, padding:'20px 24px', border:`1px solid ${!adMeaningful?C.textFaint:adColor}`, marginBottom:SP.lg, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <p style={{ color:adColor, fontSize:S.small, fontWeight:W.bold, marginBottom:SP.xs }}>{adLabel}</p>
          <p style={{ color:C.text, fontSize:S.h3 }}>
            EPS: <span style={{ textDecoration:'line-through', color:C.textFaint }}>{fmtRs(ad.eps_before)}</span>
            <span style={{ margin:'0 8px', color:C.textFaint }}>→</span>
            <span style={{ color:adColor, fontWeight:W.bold }}>{fmtRs(ad.eps_after)}</span>
          </p>
          {!adMeaningful && (
            <p style={{ color:C.textMuted, fontSize:S.small, marginTop:SP.sml }}>
              {isImplausible
                ? 'Accretion and dilution aren\'t meaningful for an implausibly sized deal.'
                : 'Accretion and dilution aren\'t meaningful when the acquirer makes a loss on its own.'}
            </p>
          )}
          {/* C1. AND WHERE THE TARGET IS WHAT MAKES THE FIGURE HARD TO READ. The test
              above asks only about the acquirer; earnings bought out of a loss-making or
              one-off-inflated target move pro forma EPS exactly as much as clean ones do. */}
          {ad.target_earnings_note && (
            <p style={{ color:C.textMuted, fontSize:S.small, marginTop:SP.sml }}>
              {ad.target_earnings_note}
            </p>
          )}
        </div>
        <div style={{ textAlign:'right' }}>
          <p style={{ color:adColor, fontSize:S.figure, fontWeight:W.bold }}>{!adMeaningful ? 'n.m.' : `${ad.eps_change_pct>0?'+':''}${ad.eps_change_pct?.toFixed(2)}%`}</p>
          <Tooltip text={METRIC_DEFINITIONS['EPS change']}>
            <p style={{ color:C.textFaint, fontSize:S.small, cursor:'help' }}>{treatment === 'equity_method' ? 'EPS change (including share of target profit)' : 'EPS change'}</p>
          </Tooltip>
        </div>
      </div>
      )}
      {treatment === 'investment' && <OwnershipSuppressedBlock code={sup.accretion_dilution} reason={supReason(sup.accretion_dilution)} items="The EPS effect" />}
      <OwnershipLadderPanel reg={ownershipRegulatory} />
      <RegulatoryFlagsCard flags={regulatoryFlags} />
      <SastFloorPanel sast={sastVwapLeg} ticker={c2.ticker} />
      <SectionLabel text="Deal Structure" />
      <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        {ownPct < 100 && <DealRow label="Ownership acquired" value={`${ownPct}%`} />}
        <DealRow label="Deal value"               value={fmt(ds.deal_value)} />
        <DealRow label="Cash component"           value={<>{Math.round(ds.cash_pct*100)}% — {fmt(ds.cash_component)}</>} />
        <DealRow label="Stock component"          value={<>{Math.round((1-ds.cash_pct)*100)}% — {fmt(ds.stock_component)}</>} />
        <DealRow label="New shares issued"        value={ds.new_shares_issued>0?`${fmtIndianAbs(ds.new_shares_issued/1e7, 2)} cr shares`:'—'} />
        <DealRow label="Share dilution"           value={ad.dilution_pct>0?`${ad.dilution_pct}%`:'—'} />
        <DealRow label="New annual interest cost" value={ds.new_annual_interest>0?fmt(ds.new_annual_interest):'—'} />
        {/* FIX 1: the figure that reaches net income. The row above is the cash the lender is
            paid, which is what interest coverage is tested on; this is that figure after the tax
            the interest saves, which is what pro forma EPS is struck on. */}
        <DealRow label="New annual interest, after tax" value={ds.new_annual_interest_after_tax>0?fmt(ds.new_annual_interest_after_tax):'—'} />
        <DealRow label="Goodwill created"         value={ds.goodwill == null
          ? <Tooltip naLabel="Why not shown?" naReason={NA_REASONS[sup.goodwill]}><span style={{ color:C.textFaint }}>n.a.</span></Tooltip>
          : fmt(ds.goodwill)} />
      </div>
      {treatment === 'equity_method' && <EquityMethodSection proforma={proforma} />}
      {!consolidated && (
        <OwnershipSuppressedBlock code={sup.revenue_proforma} reason={supReason(sup.revenue_proforma)}
          items={treatment === 'equity_method'
            ? 'Combined revenue, combined EBITDA, synergies, the pro forma balance sheet and the deal characteristics'
            : 'The pro forma income statement, synergies, the pro forma balance sheet and the deal characteristics'} />
      )}
      {consolidated && (<>
      <SectionLabel text="Pro Forma Income Statement" />
      <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', padding:'12px 20px', backgroundColor:C.surface, borderBottom:`1px solid ${C.line}` }}>
          <span style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi }}>Metric</span>
          <span style={{ color:C.accent, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{c1.ticker}</span>
          <span style={{ color:C.target, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>{c2.ticker}</span>
          <span style={{ color:C.text,   fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>Pro forma</span>
        </div>
        {[
          ['Revenue',            fmt(is_.revenue_c1), fmt(is_.revenue_c2), fmt(is_.revenue_proforma)],
          ['+ Revenue synergy',  '',                  '',                  <>+{fmt(is_.revenue_synergy)}</>],
          ['EBITDA',             fmt(is_.ebitda_c1),  fmt(is_.ebitda_c2),  fmt(is_.ebitda_proforma)],
          // THE FIGURE THE PRO FORMA COLUMN ACTUALLY ADDS. This row said 'Cost synergy' and
          // showed the cost synergy alone, while the EBITDA above it included the revenue
          // synergy's margin contribution as well - so the column did not add up, and it
          // disagreed with tearsheet page 6 and Merger Model E58. Same figure, same name,
          // all three surfaces; the breakdown is on the sub-line below the table.
          //
          // AND WHERE THERE IS NO EBITDA - every bank, NBFC and insurer - there is no EBITDA
          // synergy to show: the row becomes the cost synergy and the after-tax figure, which
          // are what the model actually sizes. The reason is on the sub-line.
          ...(ebitdaSynergyOk
            ? [['+ EBITDA synergy', '', '', <>+{fmt(is_.ebitda_synergy)}</>]]
            : [['+ Cost synergy', '', '', <>+{fmt(syn.cost_synergy)}</>]]),
          // EVERY LINE OF THE BRIDGE, so the column adds to the figure printed at the bottom of
          // it. ni_proforma is ni_c1 + ni_c2 + ni_synergy - interest after tax; the last two rows
          // were missing here while tearsheet page 6 showed them, so the column was short by the
          // synergy and over by the interest. Same order and same wording as page 6.
          ['+ Net income synergy, after tax', '', '', <>+{fmt(is_.ni_synergy)}</>],
          ['Less: new acquisition interest, after tax', '', '',
           <>({fmt(ds.new_annual_interest_after_tax)})</>],
          ['Net income',         fmt(is_.ni_c1),      fmt(is_.ni_c2),      fmt(is_.ni_proforma)],
          // Below 100%: the NCI on its own line, then the figure EPS is computed on.
          ...(withNci ? [
            ['Less: non-controlling interest',    '', '', <>({fmt(is_.ni_nci)})</>],
            ['Net income attributable to owners', '', '', fmt(is_.ni_attributable)],
          ] : []),
        ].map(([label, v1, v2, vpf]) => {
          const isSynergy = label.startsWith('+');
          return (
            <div key={label} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
              <span style={{ color:isSynergy?C.accent:C.textMuted, fontSize:S.body }}>
                {!isSynergy && METRIC_DEFINITIONS[label]
                  ? <MetricLabel label={label} />
                  : label}
              </span>
              <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>{v1}</span>
              <span style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>{v2}</span>
              <span style={{ color:isSynergy?C.accent:C.text, fontSize:S.body, textAlign:'right', fontWeight:W.semi }}>{vpf}</span>
            </div>
          );
        })}
      </div>
      <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.5, marginTop:`-${SP.sm}`, marginBottom:SP.lg }}>
        {ebitdaSynergyOk
          ? <>The EBITDA synergy is the cost synergy of {fmt(is_.cost_synergy)} plus the revenue synergy of {fmt(is_.revenue_synergy)} at the combined EBITDA margin. That adds {fmt(syn.rev_synergy_ebitda)}. The net income line carries the same synergy after tax at {Math.round((syn.tax_rate ?? 0.25) * 100)}%, less the interest on the cash half of the consideration. That interest is {fmt(ds.new_annual_interest)} a year before tax and {fmt(ds.new_annual_interest_after_tax)} after it.</>
          : <>{syn.ebitda_synergy_unavailable_reason} The net income line carries the cost synergy after tax at {Math.round((syn.tax_rate ?? 0.25) * 100)}%, less the interest on the cash half of the consideration. That interest is {fmt(ds.new_annual_interest)} a year before tax and {fmt(ds.new_annual_interest_after_tax)} after it.</>}
      </p>
      {withNci && (
        <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.5, marginTop:`-${SP.sm}`, marginBottom:SP.lg }}>
          The target is consolidated in full. The non-controlling interest takes {100 - ownPct}% of its net income and of the after-tax synergy.
        </p>
      )}
      {is_.ebitda_unavailable_reason && (
        <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.5, marginTop:`-${SP.sm}`, marginBottom:SP.lg }}>
          {is_.ebitda_unavailable_reason}
        </p>
      )}
      <SectionLabel text="Synergy Estimates" />
      <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        <div style={{ padding:'10px 20px', backgroundColor:C.surface, borderBottom:`1px solid ${C.lineSoft}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ color:C.textFaint, fontSize:S.small }}>Benchmarks based on the acquirer's sector</span>
          <span style={{ color:C.target, fontSize:S.small, fontWeight:W.semi }}>{syn.acquirer_sector||'Default'}</span>
        </div>
        {[
          [`Cost synergies (${syn.cost_synergy_pct??5}% of target revenue)`, fmt(syn.cost_synergy),    'Cost synergies'],
          [`Revenue synergies (${syn.rev_synergy_pct??2}% of target revenue)`,fmt(syn.revenue_synergy), 'Revenue synergies'],
          ['Total synergy value', fmt(syn.total_synergy), 'Total synergy value'],
        ].map(([label, value, defKey]) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
            <span style={{ color:C.textMuted, fontSize:S.body }}><MetricLabel label={label} defKey={defKey} /></span>
            <span style={{ color:C.accent, fontSize:S.body, fontWeight:W.semi }}>{value}</span>
          </div>
        ))}
      </div>
      <SectionLabel text="Pro Forma Balance Sheet" />
      <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
        {[
          ['Combined market capitalisation',   fmt(bs.combined_market_cap), C.text],
          ['Combined total assets', fmt(bs.combined_assets), C.text],
          ['Combined total debt',   fmt(bs.combined_debt), C.text],
          ['Combined cash',         bs.combined_cash < 0 ? <>({fmt(Math.abs(bs.combined_cash))}): cash shortfall</> : fmt(bs.combined_cash), bs.combined_cash < 0 ? C.negative : C.text],
          ['Goodwill on books',     fmt(bs.goodwill), C.text],
          ...(withNci ? [['Non-controlling interest', fmt(bs.nci_equity), C.text]] : []),
        ].map(([label, value, color]) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'12px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
            <span style={{ color:C.textMuted, fontSize:S.body }}><MetricLabel label={label} /></span>
            <span style={{ color: color, fontSize:S.body, fontWeight:W.medium }}>{value}</span>
          </div>
        ))}
      </div>
      </>)}
      <div style={{ backgroundColor:C.surface, borderRadius:R.sm, padding:'14px 18px', border:`1px solid ${C.lineSoft}`, marginBottom:SP.xl }}>
        <p style={{ color:C.textFaint, fontSize:S.small }}>⚠️ Synergies are rule-based sector benchmarks. {premium}% offer premium, {ownership}% of the equity acquired and an 8% debt rate assumed.</p>
      </div>
      {/* THE DEAL'S TAX POSITION. The pro forma above carries no tax step for the deal, and this
          is the block that says what the pro forma assumes, so it is where a reader asking what
          happens to the target's losses will be. No figure is shown, and the note says why. */}
      {regulatoryNotes && regulatoryNotes.section_72a && (
        <div style={{ backgroundColor:C.surface, borderRadius:R.sm, padding:'14px 18px', border:`1px solid ${C.lineSoft}`, marginBottom:SP.xl }}>
          <p style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, margin:`0 0 ${SP.xs} 0` }}>
            The Deal's Tax Position<InfoMarker id="carry-forward-of-losses" />
          </p>
          <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.6, margin:0 }}>{regulatoryNotes.section_72a}</p>
        </div>
      )}

      <SectionLabel text={`Comparable Companies: ${c2.ticker} (target)`} />

      {/* Peer chips + add input */}
      <div style={{ marginBottom:SP.lg }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:SP.sm, marginBottom:SP.med }}>
          {(compsData?.peer_tickers || []).map(t => (
            <span key={t} style={{ display:'inline-flex', alignItems:'center', gap:SP.sml, backgroundColor:'#1a2a1a', border:`1px solid ${C.accent}`, borderRadius:R.pill, padding:'5px 12px', fontSize:S.small, color:C.accent }}>
              {t}
              <span onClick={() => removePeer(t)} style={{ cursor:(compsAdding)?'not-allowed':'pointer', color:C.negative, fontWeight:W.bold, fontSize:S.body, lineHeight:1 }}>×</span>
            </span>
          ))}
          {(!compsData || (compsData.peer_tickers || []).length === 0) && (
            <span style={{ color:C.textFaint, fontSize:S.small, fontStyle:'italic' }}>No peer suggestions. Enter tickers yourself.</span>
          )}
        </div>
        {(() => {
          const pm = compsData?.peer_meta;
          if (!pm || !pm.source || pm.source === 'none') return null;
          const auto = pm.source === 'industry' || pm.source === 'sector';
          let text;
          if (pm.source === 'manual') {
            text = 'Peers entered by hand.';
          } else if (pm.source === 'curated') {
            text = `${groupLabel(pm.group)} peer group.`;
          } else if (pm.source === 'sector') {
            text = `No peer group covers this company, so these peers are matched on its sector (${pm.group}, `
                 + `${pm.candidates_considered} companies). That is too broad for a peer set, so peer statistics are held back until you edit the list.`;
          } else {
            text = `No peer group covers this company, so these peers are matched on its industry (${pm.group}, `
                 + `${pm.candidates_considered} companies).`;
          }
          const band = pm.size_band
            ? (pm.size_band === 'unbounded'
                ? ' Too few companies of similar size to narrow it further.'
                : ` Screened to within ${pm.size_band} of its market capitalisation`
                  + (pm.excluded_on_size?.length
                      ? `; ${pm.excluded_on_size.length} set aside on size (${pm.excluded_on_size.slice(0,5).join(', ')}${pm.excluded_on_size.length > 5 ? ' and others' : ''}).`
                      : '.'))
            : '';
          return (
            <p style={{ color: auto ? C.target : C.textMuted, fontSize:S.small,
                        marginTop:SP.xxs, marginBottom:0, lineHeight:1.5 }}>
              {auto ? '⚠ ' : ''}{text}{band}
            </p>
          );
        })()}
        <div style={{ display:'flex', gap:SP.sm }}>
          <TickerAutocomplete
            value={peerInput}
            onChange={setPeerInput}
            onSubmit={addPeer}
            placeholder="Add peer ticker…"
            tickers={tickers}
            style={{ backgroundColor:C.surface, border:`1px solid ${C.line}`, borderRadius:R.sm, padding:'8px 14px', color:C.text, fontSize:S.body, width:'160px', outline:'none' }}
          />
          <button onClick={addPeer} disabled={compsAdding} style={{ backgroundColor:C.tintAccent, border:`1px solid ${C.accent}`, borderRadius:R.sm, padding:'8px 16px', color:C.accent, fontSize:S.body, fontWeight:W.semi, cursor:(compsAdding)?'not-allowed':'pointer' }}>
            {compsAdding ? '…' : '+ Add'}
          </button>
        </div>
        {compsError && <p style={{ color:C.negative, fontSize:S.small, marginTop:SP.sm }}>{compsError}</p>}
      </div>

      {compsData && (() => {
        const cd = compsData;
        const isFin = cd.is_financial;
        const multKeys = isFin ? ['pb','pe'] : ['ev_ebitda','ev_revenue','pe'];
        const multLabels = { ev_ebitda:'EV/EBITDA', ev_revenue:'EV/Revenue', pe:'P/E', pb:'P/B' };
        const gridCols = `2fr ${multKeys.map(()=>'1fr').join(' ')}`;
        const fmtMult = (v) => {
          if (v === null || v === undefined) return null;
          try { if (isNaN(v)) return null; } catch { return null; }
          return Number(v).toFixed(1) + 'x';
        };
        const multCell = (v, reason) => {
          const f = fmtMult(v);
          if (f && reason === 'outlier') {
            return <Tooltip naLabel="Why is this excluded?" naReason={NA_REASONS['comps_outlier']}><span style={{ color:C.outlier }}>{f}</span></Tooltip>;
          }
          if (f) return f;
          if (reason === 'nm_negative_base') return <Tooltip naLabel="Why not meaningful?" naReason={NA_REASONS['comps_nm_negative_base']}><span style={{ color:C.textFaint }}>n.m.</span></Tooltip>;
          if (reason === 'nm_extreme')       return <Tooltip naLabel="Why not meaningful?" naReason={NA_REASONS['comps_nm_extreme']}><span style={{ color:C.textFaint }}>n.m.</span></Tooltip>;
          if (reason === 'nm_negative_ev')   return <Tooltip naLabel="Why not meaningful?" naReason={NA_REASONS['comps_nm_negative_ev']}><span style={{ color:C.textFaint }}>n.m.</span></Tooltip>;
          if (reason === 'nr')               return <Tooltip naLabel="Why not reported?" naReason={NA_REASONS['comps_nr']}><span style={{ color:C.textFaint }}>NR</span></Tooltip>;
          if (reason === 'fx_unpinned')      return <Tooltip naLabel="Why left out?" naReason={NA_REASONS['comps_fx_unpinned']}><span style={{ color:C.textFaint }}>n.a.</span></Tooltip>;
          return <Tooltip naLabel="Why N/A?" naReason={NA_REASONS['comps_multiple_unavailable']}><span style={{ color:C.textFaint }}>N/A</span></Tooltip>;
        };
        const statCell = (stat, key) => {
          if (stat && stat.suppressed === 'broad_match') return <Tooltip naLabel="Why no median?" naReason={NA_REASONS['comps_broad_match']}><span style={{ color:C.textFaint }}>—</span></Tooltip>;
          if (!stat || stat.insufficient) {
            const c = (stat && stat.count) || 0;
            const n = (stat && stat.outliers_excluded) || 0;
            let why = `Only ${c} peer${c === 1 ? '' : 's'} produced a usable value for this multiple. A median needs at least three.`;
            if (n > 0) why += ` Another ${n} had a multiple far outside the useful range and ${n === 1 ? 'was' : 'were'} left out.`;
            return <Tooltip naLabel="Why N/A?" naReason={why}><span style={{ color:C.textFaint }}>N/A</span></Tooltip>;
          }
          return stat[key] != null ? stat[key].toFixed(1) + 'x' : 'N/A';
        };

        const hk = cd.headline_key;
        const pd_ = cd.premium_discount || {};
        const hPd = pd_[hk] || {};
        const hlLabel = multLabels[hk];
        let headlineText = null;
        const hlStat = cd.stats[hk];
        const hlCur  = cd.target_current[hk];
        if (hlStat && hlStat.suppressed === 'broad_match') {
          headlineText = `No peer median is shown for ${hlLabel}. These peers come from a broad sector match, not a curated group. Edit the peer list above and the statistics will appear.`;
        } else if (!hlStat || hlStat.insufficient) {
          headlineText = `No peer median ${hlLabel}: not enough comparable data.`;
        } else if (hlCur == null) {
          // THE REASON WAS IN THE PAYLOAD THE WHOLE TIME. This sentence used to stop at "isn't
          // meaningful", which is the one line a reader reads first and the only one that did
          // not say why - while the cell below it carried the full reason in a tooltip.
          headlineText = `The target's current ${hlLabel} isn't meaningful, because ${nmClause(cd.target_current[hk + '_reason'])}. The peer median is ${hlStat.median?.toFixed(1)}x.`;
        } else if (hPd.current_vs_median != null && hPd.deal_vs_median != null) {
          const curWord = hPd.current_vs_median >= 0 ? 'premium' : 'discount';
          const dealWord = hPd.deal_vs_median >= 0 ? 'premium' : 'discount';
          const artCur  = /^(8|11|18|8\d)$/.test(String(Math.round(Math.abs(hPd.current_vs_median)))) ? 'an' : 'a';
          const artDeal = /^(8|11|18|8\d)$/.test(String(Math.round(Math.abs(hPd.deal_vs_median)))) ? 'an' : 'a';
          headlineText = `The target trades at ${artCur} ${Math.abs(hPd.current_vs_median).toFixed(0)}% ${curWord} to the peer median ${hlLabel}. At the offer price it is at ${artDeal} ${Math.abs(hPd.deal_vs_median).toFixed(0)}% ${dealWord}.`;
        }

        return (
          <>
            {headlineText && (
              <div style={{ backgroundColor:'#1a1a2a', borderRadius:R.sm, padding:'12px 16px', border:`1px solid ${C.line}`, marginBottom:SP.lg }}>
                <p style={{ color:'#9aa3ad', fontSize:S.body, lineHeight:1.5, margin:0 }}>{headlineText}</p>
              </div>
            )}

            {compsAdding && <p style={{ color:C.textFaint, fontSize:S.small, marginBottom:SP.med }}>Adding peer…</p>}

            <div style={{ backgroundColor:C.lineSoft, borderRadius:R.md, overflow:'hidden', border:`1px solid ${C.line}`, marginBottom:SP.sm }}>
              {/* Header */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', backgroundColor:C.surface, borderBottom:`1px solid ${C.line}` }}>
                <span style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi }}>Company</span>
                {multKeys.map(k => (
                  <span key={k} style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, textAlign:'right' }}>
                    <Tooltip text={`Trailing ${multLabels[k]}`}>{multLabels[k]}</Tooltip>
                  </span>
                ))}
              </div>
              {/* Peer rows + target inline */}
              {cd.peers.map((p, i) => (
                <div key={p.ticker} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 20px', borderBottom:`1px solid ${C.lineSoft}`, backgroundColor:i%2===0?'transparent':C.surface }}>
                  <span style={{ color:C.textMuted, fontSize:S.body }}>{p.name || p.ticker}</span>
                  {multKeys.map(k => (
                    <span key={k} style={{ color:C.text, fontSize:S.body, textAlign:'right' }}>{multCell(p[k], p[k+'_reason'])}</span>
                  ))}
                </div>
              ))}
              {/* Target inline row */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 20px', borderBottom:`1px solid ${C.line}`, backgroundColor:C.tintTarget, borderLeft:`3px solid ${C.target}` }}>
                <span style={{ color:C.target, fontSize:S.body, fontWeight:W.semi }}>{cd.target_name || cd.target_ticker || c2.ticker}</span>
                {multKeys.map(k => (
                  <span key={k} style={{ color:C.target, fontSize:S.body, fontWeight:W.semi, textAlign:'right' }}>{multCell(cd.target_current[k], cd.target_current[k+'_reason'])}</span>
                ))}
              </div>
              {/* Median row */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', borderBottom:`1px solid ${C.textFaint}`, backgroundColor:C.tintAccent }}>
                <span style={{ color:C.accent, fontSize:S.body, fontWeight:W.bold }}>Median</span>
                {multKeys.map(k => (
                  <span key={k} style={{ color:C.accent, fontSize:S.body, fontWeight:W.bold, textAlign:'right' }}>{statCell(cd.stats[k], 'median')}</span>
                ))}
              </div>
              {/* Mean row */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
                <span style={{ color:C.textMuted, fontSize:S.small }}>Mean</span>
                {multKeys.map(k => (
                  <span key={k} style={{ color:C.textMuted, fontSize:S.small, textAlign:'right' }}>{statCell(cd.stats[k], 'mean')}</span>
                ))}
              </div>
              {/* Range row */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 20px', borderBottom:`1px solid ${C.lineSoft}` }}>
                <span style={{ color:C.textMuted, fontSize:S.small }}>Range</span>
                {multKeys.map(k => {
                  const s = cd.stats[k];
                  if (!s || s.insufficient || s.suppressed || s.min == null || s.max == null) return <span key={k} style={{ color:C.textFaint, fontSize:S.small, textAlign:'right' }}>—</span>;
                  return <span key={k} style={{ color:C.textMuted, fontSize:S.small, textAlign:'right' }}>{s.min?.toFixed(1)}x – {s.max?.toFixed(1)}x</span>;
                })}
              </div>
              {/* Target current row */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px', borderBottom:`1px solid ${C.line}`, borderTop:`2px solid ${C.line}` }}>
                <span style={{ color:C.target, fontSize:S.body, fontWeight:W.semi }}>{c2.ticker} (current)</span>
                {multKeys.map(k => (
                  <span key={k} style={{ color:C.target, fontSize:S.body, fontWeight:W.semi, textAlign:'right' }}>{multCell(cd.target_current[k], cd.target_current[k+'_reason'])}</span>
                ))}
              </div>
              {/* Target implied row */}
              <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'12px 20px' }}>
                <span style={{ color:C.offer, fontSize:S.body, fontWeight:W.semi }}>{c2.ticker} (at offer price)</span>
                {multKeys.map(k => (
                  <span key={k} style={{ color:C.offer, fontSize:S.body, fontWeight:W.semi, textAlign:'right' }}>{multCell(cd.target_implied[k], cd.target_implied[k+'_reason'])}</span>
                ))}
              </div>
            </div>
            <p style={{ color:C.textFaint, fontSize:S.small, marginTop:'-2px', marginBottom:SP.xlr }}>
              Trading multiples use each company's latest annual figures. The median, mean and range leave out loss-makers, meaningless ratios and outliers, which are still shown and marked.
            </p>
            {(() => {
              const parts = [];
              multKeys.forEach(k => {
                const t = ((cd.stats[k] || {}).outlier_tickers || []).filter(Boolean);
                if (t.length) parts.push(`${multLabels[k]}: ${t.join(', ')}`);
              });
              if (!parts.length) return null;
              const suppressed = multKeys.some(k => (cd.stats[k] || {}).suppressed === 'broad_match');
              return (
                <p style={{ color:C.outlier, fontSize:S.small, marginTop:'-14px', marginBottom:SP.xlr }}>
                  {suppressed
                    ? `Marked in amber: multiples far outside the useful range for a trading multiple (${parts.join('; ')}). No peer statistics are published for this peer set, so nothing was left out of a calculation.`
                    : `Left out as statistical outliers, by multiple: ${parts.join('; ')}. Their other multiples still count towards their own medians.`}
                </p>
              );
            })()}
          </>
        );
      })()}

      <div style={{ backgroundColor:C.surface, borderRadius:R.sm, padding:'14px 18px', border:`1px solid ${C.lineSoft}` }}>
        <p style={{ color:C.textFaint, fontSize:S.small }}>⚠️ Synergies are rule-based sector benchmarks. {premium}% offer premium, {ownership}% of the equity acquired and an 8% debt rate assumed.</p>
      </div>
    </div>
  );
}

function AccountMenu({ user, onSignOut, showDialog, onReopen }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const initial = (user.email || '?').trim().charAt(0).toUpperCase();

  const loadHistory = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/history`, { credentials: 'include' });
      const d = await r.json();
      setHistory(d.events || []);
    } catch { setHistory([]); }
  };

  return (
    <div style={{ position:'relative' }}>
      <div onClick={() => setOpen(!open)}
           title={user.email}
           style={{ width:'34px', height:'34px', borderRadius:'50%', backgroundColor:C.tintAccent,
                    border:`1px solid ${C.accent}`, color:C.accent, display:'flex',
                    alignItems:'center', justifyContent:'center', cursor:'pointer',
                    fontSize:S.body, fontWeight:W.semi, fontFamily:F.ui, userSelect:'none' }}>
        {initial}
      </div>
      {open && (
        <>
          <div onClick={() => setOpen(false)}
               style={{ position:'fixed', inset:0, zIndex:60 }} />
          <div style={{ position:'absolute', top:'42px', right:0, zIndex:61, minWidth:'220px',
                        backgroundColor:C.surface, border:`1px solid ${C.line}`, borderRadius:R.md,
                        padding:SP.sm, boxShadow:'0 8px 28px rgba(0,0,0,0.6)' }}>
            <p style={{ color:C.textMuted, fontSize:S.small, margin:`0 0 ${SP.sm} 0`,
                        padding:`0 ${SP.sm}`, wordBreak:'break-all' }}>{user.email}</p>
            <div style={{ height:'1px', backgroundColor:C.line, margin:`${SP.sm} 0` }} />
            <p onClick={loadHistory}
               style={{ color:C.textMuted, fontSize:S.small, margin:0, padding:`${SP.sm} ${SP.sm}`,
                        cursor:'pointer', borderRadius:R.sm }}>Recent exports</p>
            {history !== null && (
              <div style={{ maxHeight:'220px', overflowY:'auto', margin:`${SP.sm} 0` }}>
                {history.length === 0 ? (
                  <p style={{ color:C.textFaint, fontSize:S.small, margin:0,
                               padding:`0 ${SP.sm}` }}>No exports yet.</p>
                ) : history.map((ev, i) => (
                  /* P6. CLICK AN ENTRY TO REOPEN THE DEAL. usage_events stores kind, the two
                     tickers, credits and the time - and NOT the premium or the ownership, so
                     those are not restored and the analysis runs at whatever is on screen.
                     An entry with no tickers is not clickable rather than silently doing
                     nothing. */
                  <div key={i}
                       onClick={ev.ticker1 && ev.ticker2 && onReopen
                         ? () => { setOpen(false); onReopen(ev.ticker1, ev.ticker2); }
                         : undefined}
                       style={{ padding:`${SP.sml} ${SP.sm}`,
                                cursor: (ev.ticker1 && ev.ticker2 && onReopen) ? 'pointer' : 'default',
                                borderTop: i > 0 ? `1px solid ${C.lineSoft}` : 'none' }}>
                    <p style={{ color:C.text, fontSize:S.small, margin:`0 0 2px 0` }}>
                      {ev.ticker1} / {ev.ticker2} — {ev.kind}
                    </p>
                    <p style={{ color:C.textFaint, fontSize:S.micro, margin:0 }}>
                      {fmtDateObj(new Date(ev.created_at))}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div style={{ height:'1px', backgroundColor:C.line, margin:`${SP.sm} 0` }} />
            <p onClick={() => {
                 setOpen(false);
                 showDialog({ title:'Sign out?', body:`You can sign back in at any time. Your export history stays on your ${PRODUCT_NAME} account.`, confirmLabel:'Sign out', cancelLabel:'Stay signed in', tone:'danger', onConfirm: onSignOut });
               }}
               style={{ color:C.text, fontSize:S.body, margin:0, padding:`${SP.sm} ${SP.sm}`,
                        cursor:'pointer', borderRadius:R.sm }}>Sign out</p>
          </div>
        </>
      )}
    </div>
  );
}

// Every browser alert() and confirm() renders a white OS box titled "localhost:3000 says",
// which looks nothing like the product. This replaces all of them with one styled dialog.
// It is deliberately generic: title, body, and one or two buttons.
function Dialog({ dialog, onClose }) {
  if (!dialog) return null;
  const { title, body, confirmLabel = 'OK', cancelLabel, onConfirm, tone } = dialog;
  const accent = tone === 'danger' ? C.negative : C.accent;
  return (
    <div onClick={onClose}
         style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.72)', zIndex:10001,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()}
           style={{ backgroundColor:C.surface, border:`1px solid ${C.line}`, borderRadius:R.md,
                    padding:`${SP.xl} ${SP.xl}`, width:'420px', maxWidth:'90vw',
                    boxShadow:'0 12px 40px rgba(0,0,0,0.7)' }}>
        {title && (
          <p style={{ fontFamily:F.display, fontSize:S.lede, color:C.text,
                      margin:`0 0 ${SP.md} 0` }}>{title}</p>
        )}
        {body && (
          <p style={{ color:C.textMuted, fontSize:S.body, lineHeight:1.65,
                      margin:`0 0 ${SP.xl} 0` }}>{body}</p>
        )}
        <div style={{ display:'flex', gap:SP.sm, justifyContent:'flex-end' }}>
          {cancelLabel && (
            <button onClick={onClose}
                    style={{ padding:'10px 18px', backgroundColor:'transparent',
                             border:`1px solid ${C.line}`, borderRadius:R.sm, color:C.textMuted,
                             fontSize:S.body, fontFamily:F.ui, cursor:'pointer' }}>
              {cancelLabel}
            </button>
          )}
          <button onClick={() => { if (onConfirm) onConfirm(); onClose(); }}
                  style={{ padding:'10px 20px', backgroundColor:accent, color:C.bg, border:'none',
                           borderRadius:R.sm, fontSize:S.body, fontWeight:W.semi, fontFamily:F.ui,
                           cursor:'pointer' }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuthModal({ open, onClose, onAuthed, onWelcome }) {
  const [mode, setMode]         = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');
  const [showPw, setShowPw]     = useState(false);
  if (!open) return null;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/${mode === 'login' ? 'login' : 'signup'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.message || 'Something went wrong.'); setBusy(false); return; }
      onAuthed(data.user);
      if (mode === 'signup') {
        onWelcome && onWelcome();
      }
      onClose();
    } catch {
      setErr("We couldn't reach the server. Please try again in a moment.");
    }
    setBusy(false);
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.72)',
                                    zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor:C.surface, border:`1px solid ${C.line}`,
           borderRadius:R.md, padding:'28px 30px', width:'380px', maxWidth:'90vw' }}>
        <p style={{ fontFamily:F.display, fontSize:S.lede, color:C.text, margin:`0 0 ${SP.sm} 0` }}>
          {mode === 'login' ? 'Sign in' : 'Create an account'}
        </p>
        <p style={{ color:C.textMuted, fontSize:S.small, margin:`0 0 ${SP.lg} 0`, lineHeight:1.5 }}>
          Analysis is open to everyone. Sign in to export the PDF tearsheet and the Excel model.
        </p>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
               placeholder="you@example.com" autoComplete="email"
               style={{ width:'100%', boxSizing:'border-box', padding:'11px 13px', marginBottom:SP.sm,
                        backgroundColor:C.raised, border:`1px solid ${C.line}`, borderRadius:R.sm,
                        color:C.text, fontSize:S.body, fontFamily:F.ui, outline:'none' }} />
        <div style={{ position:'relative', marginBottom:SP.md }}>
          <input type={showPw ? 'text' : 'password'} value={password}
                 onChange={e => setPassword(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && !busy && submit()}
                 placeholder={mode === 'login' ? 'Password' : 'At least 10 characters'}
                 autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                 style={{ width:'100%', boxSizing:'border-box', padding:'11px 62px 11px 13px',
                          backgroundColor:C.raised, border:`1px solid ${C.line}`, borderRadius:R.sm,
                          color:C.text, fontSize:S.body, fontFamily:F.ui, outline:'none' }} />
          <span onClick={() => setShowPw(!showPw)}
                style={{ position:'absolute', right:'13px', top:'50%', transform:'translateY(-50%)',
                         color:C.textMuted, fontSize:S.small, cursor:'pointer', userSelect:'none' }}>
            {showPw ? 'Hide' : 'Show'}
          </span>
        </div>
        {err && <p style={{ color:C.negative, fontSize:S.small, margin:`0 0 ${SP.md} 0` }}>{err}</p>}
        <button onClick={submit} disabled={busy}
                style={{ width:'100%', padding:'12px', backgroundColor:C.accent, color:C.bg,
                         border:'none', borderRadius:R.sm, fontSize:S.body, fontWeight:W.semi,
                         fontFamily:F.ui, cursor:busy ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Please wait…' : (mode === 'login' ? 'Sign in' : 'Create account')}
        </button>
        <p onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setErr(''); }}
           style={{ color:C.textMuted, fontSize:S.small, textAlign:'center',
                    marginTop:SP.md, marginBottom:0, cursor:'pointer' }}>
          {mode === 'login' ? 'No account? Create one' : 'Already have an account? Sign in'}
        </p>
      </div>
    </div>
  );
}

// The whole app, wrapped once in the explanations it reads. /methodology is a view swap,
// not a route: usePath() follows pushState and the back button, and both hosts rewrite
// every path to index.html (render.yaml's rewrite, with public/_redirects as the backstop), so
// a direct load works too.
export default function App() {
  const path = usePath();
  return (
    <ExplanationsProvider apiBase={API_BASE}>
      {/* The question box is a sibling of the page switch, not a child of any page, so one
          mount covers every path - including the methodology and Questions and Answers pages.
          The provider is what lets the deal app tell it which deal is on screen. */}
      <QuestionContextProvider>
        {path === METHODOLOGY_PATH ? <MethodologyPage productName={PRODUCT_NAME} />
          : path === QUESTIONS_PATH
            ? <QuestionsAnswersPage apiBase={API_BASE} productName={PRODUCT_NAME} />
            : path === LIMITATIONS_PATH
              ? <LimitationsPage productName={PRODUCT_NAME} />
              : <DealApp />}
        <QuestionBox apiBase={API_BASE} productName={PRODUCT_NAME} />
      </QuestionContextProvider>
    </ExplanationsProvider>
  );
}

function DealApp() {
  // G2. THE ROOT ROUTE HAD NO TITLE OF ITS OWN and no description beyond index.html's, so the
  // one page a searcher would land on was called "Dealquire" and nothing more.
  useRouteHead({
    title: 'Dealquire: Merger Analysis for NSE-listed companies',
    description: 'Build a Pro-Forma Merger Model for any two NSE-listed companies: Deal value, '
      + 'EPS accretion, Synergies, Valuation and the Regulatory thresholds that apply.',
    path: '/',
  });
  const [ticker1,   setTicker1]   = useState('');
  const [ticker2,   setTicker2]   = useState('');
  const [results,   setResults]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');
  const [dealType,     setDealType]     = useState('50_50');
  const [premium,      setPremium]      = useState(20);
  const [premiumInput, setPremiumInput] = useState('20');
  const [ownership,      setOwnership]      = useState(100);
  const [ownershipInput, setOwnershipInput] = useState('100');
  const [tickers, setTickers] = useState([]);
  const [tickerAliases, setTickerAliases] = useState({});
  // True once this analysis has been running longer than SLOW_NOTICE_MS. It carries no reason -
  // see SLOW_NOTICE. It is not an error state and never blocks anything.
  const [slow, setSlow] = useState(false);
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const showDialog = (opts) => setDialog(opts);

  // WHAT THE QUESTION BOX ATTACHES. The box floats above the page and cannot see this state,
  // so the page hands it over. openAuth is memoised deliberately: publishing a function that
  // is rebuilt on every render would make the published object differ every time and put the
  // provider into a loop.
  const publishQuestionContext = useQuestionPublisher();
  const openAuth = useCallback(() => setAuthOpen(true), []);
  useEffect(() => {
    const ds = results && results.proforma && results.proforma.deal_structure;
    publishQuestionContext({
      page: results ? 'Analysis' : 'Home',
      tab: results ? activeTab : null,
      // Only when there is an analysis on screen. Before that the ownership and premium inputs
      // are defaults nobody has looked at, and attaching them would describe a deal the reader
      // is not asking about.
      ticker1: results ? results.company1.ticker : null,
      ticker2: results ? results.company2.ticker : null,
      ownership_pct: results ? ownership : null,
      premium_pct: results ? premium : null,
      cash_pct: ds ? Math.round(ds.cash_pct * 100) : null,
      user,
      onSignIn: openAuth,
    });
  }, [publishQuestionContext, results, activeTab, ownership, premium, user, openAuth]);

  // One fetch on mount; every keystroke filters this list locally.
  useEffect(() => {
    fetch(`${API_BASE}/api/tickers`)
      .then(r => r.json())
      .then(d => { setTickers(d.tickers || []); setTickerAliases(d.aliases || {}); })
      .catch(() => {});   // autocomplete is an enhancement, never a blocker
  }, []);

  // One request to the cheapest endpoint on mount, so that whatever the host has to do before it
  // can serve a request happens while the reader is typing two tickers rather than after they
  // press the button. /api/health touches no database and fetches nothing, so this costs the
  // server nothing. Its response is not read and a failure is ignored: nothing the reader sees
  // depends on it.
  useEffect(() => {
    fetch(`${API_BASE}/api/health`).catch(() => {});
  }, []);

  // Ask the server who we are on mount. The session lives in an HttpOnly cookie, so the
  // frontend cannot read it directly - it has to ask.
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setUser(d.user || null); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Signing out and back in never reloads the page, so React state from the previous
  // session survives - a new account inherited the last user's 20%-edited-to-8% premium.
  // Whenever the signed-in identity changes this resets the premium to 20, clears the
  // results and any error.
  // The tickers are deliberately NOT cleared: someone signing in mid-session has already
  // typed them and should not have to type them again. Clearing results means they land
  // on the empty state rather than the previous user's analysis, so nothing of the last
  // account's output leaks - only the two symbols in the input boxes persist.
  // Keyed on id, not on the object, because refreshUser replaces the object and that
  // must NOT wipe what the user is working on.
  const prevUserId = useRef(undefined);
  useEffect(() => {
    const id = user ? user.id : null;
    if (prevUserId.current !== undefined && prevUserId.current !== id) {
      setPremium(20);
      setPremiumInput('20');
      setOwnership(100);
      setOwnershipInput('100');
      setResults(null);
      setError(null);
    }
    prevUserId.current = id;
  }, [user]);

  const refreshUser = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
      const d = await r.json();
      setUser(d.user || null);
    } catch {}
  };

  const analyseDeal = async (overrideDealType, overridePremium, keepTab = false, t1, t2, overrideOwnership) => {
    const dt   = overrideDealType || dealType;
    const prem = overridePremium !== undefined ? overridePremium : premium;
    const own  = overrideOwnership !== undefined ? overrideOwnership : ownership;
    const a = t1 || ticker1;
    const b = t2 || ticker2;
    if (!a || !b) { setError('Please enter both tickers'); return; }
    setLoading(true); setError(null);
    // Nothing is asked of the server about why this is slow. The notice is the same sentence
    // whatever the answer would have been. Cleared in every exit path below.
    const slowTimer = setTimeout(() => setSlow(true), SLOW_NOTICE_MS);
    try {
      const res  = await fetch(`${API_BASE}/api/analyse?ticker1=${encodeURIComponent(a)}&ticker2=${encodeURIComponent(b)}&deal_type=${dt}&premium=${prem}&ownership=${own}`);
      const data = await res.json();
      // A refusal for capacity gets the one capacity sentence, not the server's own wording, so
      // there is a single place this text lives.
      if (data.error === 'server_busy') { setError(AT_CAPACITY); setResults(null); }
      else if (data.error) { setError(data.message || data.error); setResults(null); }
      else { setResults(data); if (!keepTab) setActiveTab('Overview'); }
    } catch {
      setError("We couldn't reach the server. Please try again in a moment.");
    }
    clearTimeout(slowTimer);
    setSlow(false);
    setLoading(false);
  };

  const handleDealTypeChange = (newType) => {
    setDealType(newType);
    analyseDeal(newType, undefined, true);
  };

  const handlePremiumChange = (newValue) => {
    setPremium(newValue);
    analyseDeal(undefined, newValue, true);
  };

  const handleOwnershipChange = (newValue) => {
    setOwnership(newValue);
    analyseDeal(undefined, undefined, true, undefined, undefined, newValue);
  };

  return (
    <div style={{ position:'relative', minHeight:'100vh', backgroundColor:C.bg, display:'flex', flexDirection:'column', alignItems:'center', paddingTop: results ? '28px' : '60px', paddingBottom: results ? '90px' : '320px', fontFamily:F.ui, color:C.text, backgroundImage:BG_FIELD }}>
      <div style={{ position:'absolute', top:SP.xlr, right:SP.xl, display:'flex',
                    alignItems:'center', gap:SP.md, zIndex:50 }}>
        {/* Both open in a new tab so an analysis in progress is never lost. */}
        <a href={QUESTIONS_PATH} target="_blank" rel="noopener noreferrer"
           style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, textDecoration:'none' }}>
          Questions and Answers
        </a>
        {/* F1. THE SITE HAS NO FOOTER, so the limitations page is linked from the header
            beside the other two standing pages. */}
        <a href={LIMITATIONS_PATH} target="_blank" rel="noopener noreferrer"
           style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, textDecoration:'none' }}>
          Limitations
        </a>
        <a href={METHODOLOGY_PATH} target="_blank" rel="noopener noreferrer"
           style={{ color:C.textMuted, fontSize:S.small, fontWeight:W.semi, textDecoration:'none' }}>
          Methodology
        </a>
        {user ? (
          <AccountMenu user={user} showDialog={showDialog}
                       onReopen={(t1, t2) => {
                         setTicker1(t1); setTicker2(t2);
                         analyseDeal(undefined, undefined, false, t1, t2);
                       }}
                       onSignOut={async () => {
            await fetch(`${API_BASE}/api/auth/logout`, { method:'POST', credentials:'include' });
            setUser(null);
          }} />
        ) : (
          <span onClick={() => setAuthOpen(true)}
                style={{ color:C.accent, fontSize:S.small, cursor:'pointer', fontWeight:W.semi }}>Sign in</span>
        )}
      </div>
      <Dialog dialog={dialog} onClose={() => setDialog(null)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onAuthed={(u) => { setUser(u); refreshUser(); }}
        onWelcome={() => showDialog({
          title: 'Your account is ready',
          body: 'You can now export the PDF tearsheet and the Excel model for any deal you analyse. Your exports are listed in the account menu, so you can find them again.',
          confirmLabel: 'Start analysing',
        })} />
      <h1 className="rise d1" style={{ fontFamily:F.display, fontSize: results ? S.lede : S.display, fontWeight:W.normal, letterSpacing:'0.01em', marginBottom: results ? SP.lg : SP.sm, color: results ? C.textMuted : C.text, transition:'font-size 0.3s ease, color 0.3s ease' }}>{PRODUCT_NAME}</h1>
      {!results && <p className="rise d2" style={{ color:C.textMuted, fontSize:S.body, marginBottom:SP.xxl }}>Pro forma Merger Model for any two NSE-listed companies</p>}
      <div style={{ display:'flex', gap:SP.xlr, marginBottom: results ? '14px' : '30px' }}>
        {[['t1', ticker1, setTicker1, 'Ticker 1: acquirer, for example RELIANCE'],
          ['t2', ticker2, setTicker2, 'Ticker 2: target, for example TCS']].map(([id, val, setter, ph]) => (
          <TickerAutocomplete
            key={id}
            value={val}
            onChange={(v) => { setter(v); if (error) setError(null); }}
            onSubmit={() => { if (!loading) analyseDeal(); }}
            placeholder={ph}
            tickers={tickers}
            aliases={tickerAliases}
            disabled={loading}
            style={{ padding:'14px 20px', fontSize:S.h3, borderRadius:R.sm,
                     border:`1px solid ${C.line}`, backgroundColor:C.lineSoft, color:C.text,
                     width:'240px', outline:'none' }}
          />
        ))}
      </div>
      <button onClick={() => analyseDeal()} disabled={loading} style={{ padding:'14px 40px', fontSize:S.h3, marginBottom: results ? SP.xlr : SP.xxl, fontWeight:W.bold, backgroundColor:loading?C.textFaint:C.accent, color:C.bg, border:'none', borderRadius:R.sm, cursor:loading?'not-allowed':'pointer' }}>
        {loading ? 'Analysing...' : 'Analyse deal →'}
      </button>
      {slow && loading && (
        <p style={{ color:C.textMuted, fontSize:S.small, lineHeight:1.6, maxWidth:'34rem',
                    textAlign:'center', margin:`-${SP.lg} 0 ${SP.xlr}` }}>
          {SLOW_NOTICE}
        </p>
      )}
      {error && (() => {
        // When we know the correction, put it ON the error rather than in a second
        // line below it - two messages about the same problem, in two colours, is
        // noise. The standalone yellow hint below only shows when there is no error.
        const known = new Set(tickers.map(t => t[0]));
        const fixes = [[ticker1, setTicker1], [ticker2, setTicker2]]
          .map(([t, set]) => [t, set, closestTicker(t, tickers)])
          .filter(([t, , sug]) => sug && t && !known.has(t.toUpperCase().trim()));
        return (
          <div style={{ color:C.negative, marginBottom:SP.xlr }}>
            {error}
            {fixes.length > 0 && (
              <span>
                {' '}Did you mean{' '}
                {fixes.map(([t, set, sug], i) => (
                  <span key={sug}>
                    {i > 0 && ' and '}
                    <span onClick={() => { set(sug); setError(null); }}
                          style={{ color:C.accent, cursor:'pointer', fontWeight:W.semi,
                                   textDecoration:'underline' }}>{sug}</span>
                  </span>
                ))}?
              </span>
            )}
          </div>
        );
      })()}
      {(() => {
        // Non-blocking. The universe can lag yfinance, so a symbol missing here is
        // a hint, never a reason to refuse the analysis.
        //
        // Only suggest when the autocomplete found NOTHING. While the user is still
        // typing a prefix, the dropdown is already showing real matches - a
        // did-you-mean alongside it contradicts what they can see ("RELI" showed
        // seven Reliance companies while suggesting BEL).
        if (!tickers.length || error) return null;
        const known = new Set(tickers.map(t => t[0]));
        const hasAnyMatch = (q) => tickers.some(([sym, name]) =>
          sym.includes(q) || (name || '').toUpperCase().includes(q));
        const hints = [[ticker1, 'Ticker 1'], [ticker2, 'Ticker 2']]
          .filter(([t]) => {
            const q = (t || '').toUpperCase().trim();
            return q && q.length >= 3 && !known.has(q) && !hasAnyMatch(q);
          })
          .map(([t, label]) => [label, t.toUpperCase().trim(), closestTicker(t, tickers)])
          .filter(([, , sug]) => sug);
        if (!hints.length) return null;
        return (
          <div style={{ color:C.target, fontSize:S.body, marginBottom:SP.lg }}>
            {hints.map(([label, typed, sug]) => (
              <div key={label}>
                {label}: "{typed}" isn't on NSE. Did you mean{' '}
                <span onClick={() => (label === 'Ticker 1' ? setTicker1(sug) : setTicker2(sug))}
                      style={{ color:C.accent, cursor:'pointer', fontWeight:W.semi,
                               textDecoration:'underline' }}>{sug}</span>?
              </div>
            ))}
          </div>
        );
      })()}
      {!results && (
        <div style={{ width:'100%', maxWidth:'960px', padding:'0 20px', marginTop:SP.sm }}>
          <p className="rise d3" style={{ color:C.textMuted, fontSize:S.small, textAlign:'center',
                                          marginBottom:SP.xl, lineHeight:1.6 }}>
            {!user
              ? 'Everything here is free. Sign in to export the PDF tearsheet and the Excel model for any deal.'
              : 'Everything here is free, including the PDF tearsheet and the Excel model for any deal.'}
          </p>
          <div className="rise d3" style={{ display:'flex', flexWrap:'wrap', gap:SP.sm, justifyContent:'center', marginBottom:'72px' }}>
            {EXAMPLE_DEALS.map(([a, b, label]) => (
              <button
                key={`${a}-${b}`}
                className="chip"
                disabled={loading}
                onClick={() => { setTicker1(a); setTicker2(b); setError(null); analyseDeal(undefined, undefined, false, a, b); }}
                style={{ background:'transparent', border:`1px solid ${C.line}`, borderRadius:R.pill,
                         padding:'7px 15px', cursor:loading?'not-allowed':'pointer',
                         color:C.textMuted, fontSize:S.small, fontFamily:F.ui, whiteSpace:'nowrap' }}
                title={label}
              >
                {a} <span style={{ color:C.textFaint }}>/</span> {b}
              </button>
            ))}
          </div>

          <div className="rise d4" style={{ backgroundColor:C.bandSurface, borderTop:`1px solid ${C.bandLine}`, borderRadius:R.md, padding:'30px 28px 34px 28px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:SP.huge }}>
            {CAPABILITIES.map(([title, body]) => (
              <div key={title}>
                <div style={{ height:'1px', backgroundColor:C.line, marginBottom:SP.md }} />
                <p style={{ color:C.text, fontSize:S.small, fontWeight:W.semi, margin:`0 0 ${SP.sm} 0`, letterSpacing:'0.02em' }}>{title}</p>
                <p style={{ color:C.textFaint, fontSize:S.small, lineHeight:1.65, margin:0 }}>{body}</p>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {results && (
        <div style={{ width:'100%', maxWidth:'960px', padding:'0 20px' }}>
          <div style={{ display:'flex', gap:SP.xs, marginBottom:SP.xl, backgroundColor:C.surface, borderRadius:R.md, padding:SP.xs }}>
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex:1, padding:SP.med, fontSize:S.body, border:'none', borderRadius:R.sm, cursor:'pointer', fontWeight:activeTab===tab?W.semi:W.normal, transition:'all 0.2s', backgroundColor:activeTab===tab?C.raised:'transparent', color:activeTab===tab?C.text:C.textMuted }}>{tab}</button>
            ))}
          </div>
          {activeTab==='Overview'      && <OverviewTab      c1={results.company1} c2={results.company2} />}
          {activeTab==='Financials'    && <FinancialsTab    c1={results.company1} c2={results.company2} historicalAlignment={results.historical_alignment} />}
          {activeTab==='Valuation'     && <ValuationTab     c1={results.company1} c2={results.company2} />}
          {activeTab==='Deal Analysis' && <DealAnalysisTab  proforma={results.proforma} c1={results.company1} c2={results.company2} dealCharacteristics={results.deal_characteristics} dealType={dealType} onDealTypeChange={handleDealTypeChange} ticker1={ticker1} ticker2={ticker2} comps={results.comps} regulatoryFlags={results.regulatory_flags} sastVwapLeg={results.sast_vwap_leg} premium={premium} premiumInput={premiumInput} onPremiumInputChange={setPremiumInput} onPremiumChange={handlePremiumChange} isAnalysing={loading} tickers={tickers} user={user} onNeedAuth={() => setAuthOpen(true)} showDialog={showDialog} ownership={ownership} ownershipInput={ownershipInput} onOwnershipInputChange={setOwnershipInput} onOwnershipChange={handleOwnershipChange} ownershipRegulatory={results.ownership_regulatory} regulatoryNotes={results.regulatory_notes} />}
        </div>
      )}
    </div>
  );
}

